// Website Scraper Tab - Fetch recipes from website database
// Extracts post URL, image, and first paragraph from articles

import { useState, useCallback, useEffect, useRef } from "react";
import styles from "./WebsiteScraperTab.module.css";
import { authHeaders, apiUrl } from "../config";
import { useAuth } from "../hooks/useAuth";

// Website configurations
interface Website {
  id: string;
  name: string;
  domain: string;
}

// Available Pinterest pin templates
const AVAILABLE_TEMPLATES = [
  { id: "midnight-navy", name: "Midnight Navy", color: "#0a1628" },
  { id: "warm-cream", name: "Warm Cream", color: "#faf7f2" },
  { id: "bold-red", name: "Bold Red", color: "#c0392b" },
  { id: "forest-green", name: "Forest Green", color: "#1e3a2f" },
  { id: "royal-purple", name: "Royal Purple", color: "#2d1b4e" },
  { id: "sunset-orange", name: "Sunset Orange", color: "#ff6b35" },
  { id: "rose-pink", name: "Rose Pink", color: "#d4a5a5" },
  { id: "ocean-blue", name: "Ocean Blue", color: "#0077b6" },
];

// Available Metricool API keys (env variable names)
const AVAILABLE_METRICOOL_KEYS = [
  "METRICOOL_API_KEY",
  "METRICOOL_API_KEY_2",
  "METRICOOL_API_KEY_3",
  "METRICOOL_API_KEY_4",
  "METRICOOL_API_KEY_5",
];

interface PayhipAccount {
  id: string;
  name: string;
  blogName: string;
  isDefault: boolean;
  templates?: string[]; // Array of template IDs to use for this account (deprecated)
  templateCategories?: string[]; // R2 template categories (KK, VV, YY, ZZ, etc.)
  titleFontSizes?: Record<string, number>; // Title font sizes per category (e.g., { "KK": 65, "VV": 70 })
  metricoolApiKey?: string; // Deprecated - use metricoolKeyName instead
  pinterestBrandName?: string; // Pinterest brand name from Metricool
  metricoolKeyName?: string; // Which env key to use (e.g., "METRICOOL_API_KEY", "METRICOOL_API_KEY_2")
  pinterestBoards?: string; // Comma-separated Pinterest board names
  extensionId?: string; // Chrome extension ID for this account
}

interface MetricoolAccount {
  id: number;
  name: string;
  pinterest_brand_name: string;
  pinterest_boards: string[];
  custom_link: string;
  is_default: number;
  metricool_key_name?: string;
}

// Default websites (fallback while loading from API)
const DEFAULT_WEBSITES: Website[] = [
  { id: "cheftaling", name: "ChefTaling", domain: "cheftaling.com" },
  { id: "recipesure", name: "RecipeSure", domain: "recipesure.com" },
];

// Use local proxy to avoid CORS issues
const PROXY_URL = "/api/scraper-proxy";

interface RecipeJsonData {
  name?: string;
  summary?: string;
  servings?: string;
  prep_time?: string;
  cook_time?: string;
  total_time?: string;
  calories?: string;
  course?: string;
  cuisine?: string;
  keywords?: string[];
  notes?: string;
}

interface Recipe {
  id: number;
  title: string;
  slug: string;
  featured_image: string;
  article_content: string;
  recipe_json?: string; // JSON string containing recipe data
  category_name?: string;
  category_slug?: string;
  created_at: string;
  status: string;
}

interface ScrapedRecipe {
  id: number;
  title: string;
  url: string;
  image: string;
  firstParagraph: string;
  payhipDescription: string; // Formatted description for Payhip
  recipeInfo?: RecipeJsonData; // Parsed recipe JSON data
  category?: string;
  createdAt: string;
  payhipPosted?: boolean;  // Whether this URL has been posted to Payhip
  payhipUrl?: string;      // URL of the Payhip post
  pinUrl?: string;         // Pinterest pin image URL
  pinTitle?: string;             // Short AI-generated title for pin overlay (max 35 chars)
  pinterestTitle?: string;       // AI-generated Pinterest title
  pinterestDescription?: string; // AI-generated Pinterest description
  metricoolScheduled?: boolean;  // Whether this post has been scheduled to Metricool
  payhipAccountId?: string; // ID of the Payhip account used for this post - used for Pinterest/Metricool
}

// Chrome extension ID - loaded from database via API

// DishSnap API URL for checking Payhip posts
const DISHSNAP_API = "https://dishsnap-api.codorders.workers.dev";

// Default Modal account prefix
const DEFAULT_MODAL_PREFIX = "digitixllc--";

// Build Modal API URL dynamically
const getModalApiUrl = (prefix: string, appName: string, endpoint: string) =>
  `https://${prefix}${appName}-${endpoint}.modal.run`;

// Declare chrome types for TypeScript
declare global {
  interface Chrome {
    runtime?: {
      sendMessage: (
        extensionId: string,
        message: unknown,
        callback?: (response: unknown) => void
      ) => void;
    };
  }
  // eslint-disable-next-line no-var
  var chrome: Chrome | undefined;
}

export function WebsiteScraperTab() {
  // Get current user for tracking
  const { user } = useAuth();

  // Settings loading state
  const [isLoadingSettings, setIsLoadingSettings] = useState(true);

  // Websites state - load from API
  const [websites, setWebsitesState] = useState<Website[]>(DEFAULT_WEBSITES);
  const [selectedWebsite, setSelectedWebsite] = useState<Website>(DEFAULT_WEBSITES[0]!);
  const [apiToken, setApiTokenState] = useState("");
  const [recipes, setRecipes] = useState<ScrapedRecipe[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [selectedRecipes, setSelectedRecipes] = useState<Set<number>>(new Set());
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSettings, setShowSettings] = useState(!apiToken);

  // Payhip posting state
  const [postedRecipes, setPostedRecipes] = useState<Set<number>>(new Set());

  // Pinterest pin generation state
  const [showPayhipOnly, setShowPayhipOnly] = useState(false);
  const [hideScheduled, setHideScheduled] = useState(true); // Hide Metricool-scheduled posts by default
  const [isGeneratingPins, setIsGeneratingPins] = useState(false);
  const [generatedPins, setGeneratedPins] = useState<Map<number, string>>(new Map());

  // Pinterest metadata generation state
  const [isGeneratingMetadata, setIsGeneratingMetadata] = useState(false);

  // Metricool accounts state
  const [metricoolAccounts, setMetricoolAccounts] = useState<MetricoolAccount[]>([]);
  const [selectedMetricoolAccount, setSelectedMetricoolAccount] = useState<number | null>(null);
  const [isSchedulingMetricool, setIsSchedulingMetricool] = useState(false);
  const [newMetricoolName, setNewMetricoolName] = useState("");
  const [newMetricoolBrand, setNewMetricoolBrand] = useState("");
  const [newMetricoolBoards, setNewMetricoolBoards] = useState("");
  const [newMetricoolLink, setNewMetricoolLink] = useState("");
  const [newMetricoolKeyName, setNewMetricoolKeyName] = useState("");

  // Metricool scheduling options
  const [scheduleStartDate, setScheduleStartDate] = useState<string>("");
  const [pinsPerDay, setPinsPerDay] = useState<number>(5);
  const [useMultiDay, setUseMultiDay] = useState(false);
  const [scheduleProgress, setScheduleProgress] = useState<{ current: number; total: number; finishDate?: string } | null>(null);

  // Chrome extension state - loaded from API
  const [extensionId, setExtensionId] = useState("");
  const [extensionConnected, setExtensionConnected] = useState(false);
  const [extensionQueueCount, setExtensionQueueCount] = useState(0);
  const refreshPayhipStatusRef = useRef<() => void>(() => {}); // Ref to refresh function

  // Modal account prefix - for switching Modal accounts
  const [modalAccountPrefix, setModalAccountPrefix] = useState(DEFAULT_MODAL_PREFIX);

  // Payhip accounts from API (managed in dashboard settings)
  const [payhipAccounts, setPayhipAccounts] = useState<PayhipAccount[]>([]);
  const [selectedPayhipAccount, setSelectedPayhipAccount] = useState<string>("");

  // State for adding new account in settings
  const [newAccountName, setNewAccountName] = useState("");
  const [newAccountBlog, setNewAccountBlog] = useState("");

  // State for adding new website in settings
  const [newWebsiteName, setNewWebsiteName] = useState("");
  const [newWebsiteDomain, setNewWebsiteDomain] = useState("");

  // Category filter state
  interface Category {
    id: number;
    name: string;
    slug: string;
  }
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>(""); // Category slug
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);

  // Pinterest Templates state (R2-based templates)
  interface PinTemplate {
    id: string;
    category: string;
    url: string;
    size: number;
    uploaded: string;
  }
  const [pinTemplates, setPinTemplates] = useState<PinTemplate[]>([]);
  const [pinTemplateCategories, setPinTemplateCategories] = useState<string[]>([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [isUploadingTemplate, setIsUploadingTemplate] = useState(false);
  const [templateUploadFiles, setTemplateUploadFiles] = useState<FileList | null>(null);
  const [templateUploadCategory, setTemplateUploadCategory] = useState<string>("KK");

  // Get the currently selected account's extension ID
  const getSelectedAccountExtensionId = useCallback(() => {
    if (!selectedPayhipAccount) return extensionId; // fallback to global
    const account = payhipAccounts.find(a => a.id === selectedPayhipAccount);
    return account?.extensionId || extensionId;
  }, [selectedPayhipAccount, payhipAccounts, extensionId]);

  // Check Chrome extension connection
  const checkExtensionConnection = useCallback(() => {
    const currentExtensionId = getSelectedAccountExtensionId();
    if (!currentExtensionId || typeof chrome === "undefined" || !chrome.runtime) {
      setExtensionConnected(false);
      return;
    }

    try {
      chrome.runtime.sendMessage(
        currentExtensionId,
        { action: "ping" },
        (response: unknown) => {
          const resp = response as { success?: boolean } | undefined;
          if (resp?.success) {
            setExtensionConnected(true);
            // Get queue count
            if (chrome?.runtime) {
              chrome.runtime.sendMessage(
                currentExtensionId,
                { action: "getQueue" },
                (queueResponse: unknown) => {
                  const qResp = queueResponse as { queue?: unknown[], isPosting?: boolean } | undefined;
                  const newCount = qResp?.queue?.length || 0;
                  const isPosting = qResp?.isPosting || false;

                  // Auto-refresh when: queue is empty, not posting, and we have locally queued items
                  if (newCount === 0 && !isPosting && postedRecipes.size > 0) {
                    console.log("Queue empty & have local posted items - auto-refreshing...");
                    setTimeout(() => {
                      refreshPayhipStatusRef.current();
                      setPostedRecipes(new Set());
                    }, 2000);
                  }

                  setExtensionQueueCount(newCount);
                }
              );
            }
          } else {
            setExtensionConnected(false);
          }
        }
      );
    } catch {
      setExtensionConnected(false);
    }
  }, [getSelectedAccountExtensionId, postedRecipes.size]);

  // Check extension connection on mount and when selected account changes
  useEffect(() => {
    checkExtensionConnection();

    // Check periodically
    const interval = setInterval(checkExtensionConnection, 5000);
    return () => clearInterval(interval);
  }, [checkExtensionConnection]);

  // Fetch Metricool accounts on mount
  useEffect(() => {
    const fetchMetricoolAccounts = async () => {
      try {
        const response = await fetch(`${DISHSNAP_API}/api/metricool/accounts`, {
          headers: authHeaders(),
        });
        if (response.ok) {
          const data = await response.json() as { accounts: MetricoolAccount[] };
          setMetricoolAccounts(data.accounts || []);
          // Set default selected account
          const defaultAcc = data.accounts?.find((a: MetricoolAccount) => a.is_default === 1);
          if (defaultAcc) {
            setSelectedMetricoolAccount(defaultAcc.id);
          } else if (data.accounts && data.accounts.length > 0) {
            setSelectedMetricoolAccount(data.accounts[0]!.id);
          }
        }
      } catch (e) {
        console.log("Failed to fetch Metricool accounts:", e);
      }
    };
    fetchMetricoolAccounts();
  }, []);

  // Load scraper settings from API on mount
  useEffect(() => {
    const loadScraperSettings = async () => {
      try {
        setIsLoadingSettings(true);

        // First, initialize defaults if needed (for new users)
        await fetch(apiUrl("/api/scraper-settings/init-defaults"), {
          method: "POST",
          headers: authHeaders(),
        });

        // Fetch all settings
        const response = await fetch(apiUrl("/api/scraper-settings"), {
          headers: authHeaders(),
        });

        if (response.ok) {
          const data = await response.json() as {
            settings: { apiToken: string | null; extensionId: string | null; modalAccountPrefix: string };
            websites: Website[];
            payhipAccounts: PayhipAccount[];
          };

          // Set settings
          setApiTokenState(data.settings.apiToken || "");
          setExtensionId(data.settings.extensionId || "");
          setModalAccountPrefix(data.settings.modalAccountPrefix || DEFAULT_MODAL_PREFIX);

          // Set websites
          if (data.websites.length > 0) {
            setWebsitesState(data.websites);
            setSelectedWebsite(data.websites[0]!);
          }

          // Set Payhip accounts
          if (data.payhipAccounts.length > 0) {
            setPayhipAccounts(data.payhipAccounts);
            const defaultAcc = data.payhipAccounts.find(a => a.isDefault);
            setSelectedPayhipAccount(defaultAcc?.id || data.payhipAccounts[0]!.id);
          }

          // If API token exists, don't show settings panel
          if (data.settings.apiToken) {
            setShowSettings(false);
          }
        }
      } catch (e) {
        console.log("Failed to load scraper settings:", e);
      } finally {
        setIsLoadingSettings(false);
      }
    };
    loadScraperSettings();
  }, []);

  // Extract first paragraph from HTML content
  const extractFirstParagraph = (html: string): string => {
    // Remove HTML tags and get text
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;

    // Find first <p> tag
    const firstP = tempDiv.querySelector("p");
    if (firstP) {
      return firstP.textContent?.trim() || "";
    }

    // Fallback: get first 200 characters of text
    const text = tempDiv.textContent?.trim() || "";
    return text.slice(0, 200) + (text.length > 200 ? "..." : "");
  };

  // Just return the first paragraph - recipe JSON is sent separately
  // The Chrome extension content script handles all HTML formatting
  const formatPayhipDescription = (firstParagraph: string): string => {
    return firstParagraph;
  };

  // Parse recipe JSON to extract info object
  const parseRecipeJson = (recipeJsonStr?: string): RecipeJsonData | null => {
    if (!recipeJsonStr) return null;
    try {
      return JSON.parse(recipeJsonStr);
    } catch {
      return null;
    }
  };

  // Fetch recipes from API via proxy
  // Fetch categories for the current website
  const fetchCategories = useCallback(async () => {
    if (!apiToken) return;

    setIsLoadingCategories(true);
    try {
      const url = `${PROXY_URL}?website=${selectedWebsite.id}&domain=${encodeURIComponent(selectedWebsite.domain)}&action=categories`;
      const response = await fetch(url, {
        headers: {
          "Authorization": `Bearer ${apiToken}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setCategories(data.data);
        }
      }
    } catch (e) {
      console.log("Failed to fetch categories:", e);
    } finally {
      setIsLoadingCategories(false);
    }
  }, [apiToken, selectedWebsite.id, selectedWebsite.domain]);

  const fetchRecipes = useCallback(async (pageNum: number = 1, search?: string, category?: string) => {
    if (!apiToken) {
      setShowSettings(true);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Use proxy to avoid CORS issues
      // Pass both website ID (legacy) and domain (new) for compatibility
      let url = `${PROXY_URL}?website=${selectedWebsite.id}&domain=${encodeURIComponent(selectedWebsite.domain)}&page=${pageNum}&limit=20`;
      if (search) {
        url += `&search=${encodeURIComponent(search)}`;
      }
      if (category) {
        url += `&category=${encodeURIComponent(category)}`;
      }

      const response = await fetch(url, {
        headers: {
          "Authorization": `Bearer ${apiToken}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error("Unauthorized - check your API token");
        }
        throw new Error(`Failed to fetch recipes: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || "Failed to fetch recipes");
      }

      const fetchedRecipes: Recipe[] = data.data || [];

      // Transform to scraped format
      const scrapedRecipes: ScrapedRecipe[] = fetchedRecipes.map((recipe) => {
        const firstPara = extractFirstParagraph(recipe.article_content);
        return {
          id: recipe.id,
          title: recipe.title,
          url: `https://${selectedWebsite.domain}/${recipe.slug}/`,
          image: recipe.featured_image,
          firstParagraph: firstPara,
          payhipDescription: formatPayhipDescription(firstPara),
          recipeInfo: parseRecipeJson(recipe.recipe_json) || undefined,
          category: recipe.category_name,
          createdAt: recipe.created_at,
          payhipPosted: false,
          payhipUrl: undefined,
        };
      });

      // Check which recipes have been posted to Payhip
      if (scrapedRecipes.length > 0) {
        try {
          const urls = scrapedRecipes.map(r => r.url).join(",");
          const checkRes = await fetch(`${DISHSNAP_API}/api/payhip/posts/check?urls=${encodeURIComponent(urls)}`, {
            headers: authHeaders(),
          });
          const checkData = await checkRes.json() as {
            posted_urls?: string[];
            url_to_payhip?: Record<string, string | null>;
            url_to_pin?: Record<string, string | null>;
            url_to_pin_title?: Record<string, string | null>;
            url_to_pinterest_title?: Record<string, string | null>;
            url_to_pinterest_desc?: Record<string, string | null>;
            url_to_metricool_scheduled?: Record<string, boolean>;
            url_to_payhip_account?: Record<string, string | null>;
          };

          if (checkData.posted_urls && checkData.url_to_payhip) {
            const postedSet = new Set(checkData.posted_urls);
            for (const recipe of scrapedRecipes) {
              if (postedSet.has(recipe.url)) {
                recipe.payhipPosted = true;
                recipe.payhipUrl = checkData.url_to_payhip[recipe.url] || undefined;
                recipe.pinUrl = checkData.url_to_pin?.[recipe.url] || undefined;
                recipe.pinTitle = checkData.url_to_pin_title?.[recipe.url] || undefined;
                recipe.pinterestTitle = checkData.url_to_pinterest_title?.[recipe.url] || undefined;
                recipe.pinterestDescription = checkData.url_to_pinterest_desc?.[recipe.url] || undefined;
                recipe.metricoolScheduled = checkData.url_to_metricool_scheduled?.[recipe.url] || false;
                recipe.payhipAccountId = checkData.url_to_payhip_account?.[recipe.url] || undefined;
              }
            }
          }
        } catch (e) {
          console.log("Failed to check Payhip posts:", e);
        }
      }

      if (pageNum === 1) {
        setRecipes(scrapedRecipes);
      } else {
        // Deduplicate when loading more
        setRecipes(prev => {
          const existingIds = new Set(prev.map(r => r.id));
          const newRecipes = scrapedRecipes.filter(r => !existingIds.has(r.id));
          return [...prev, ...newRecipes];
        });
      }

      setHasMore(data.pagination?.hasMore ?? fetchedRecipes.length === 20);
      setPage(pageNum);

    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch recipes");
    } finally {
      setIsLoading(false);
    }
  }, [apiToken, selectedWebsite]);

  // Fetch categories when website changes
  useEffect(() => {
    if (apiToken && !showSettings) {
      setCategories([]);
      setSelectedCategory("");
      fetchCategories();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWebsite.id, apiToken, showSettings]);

  // Initial load - only when website changes or token is first set
  useEffect(() => {
    if (apiToken && !showSettings) {
      setRecipes([]); // Clear existing recipes when switching websites
      setPage(1);
      fetchRecipes(1, "", selectedCategory);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWebsite.id, apiToken, showSettings]);

  // Reload when category changes
  useEffect(() => {
    if (apiToken && !showSettings && !isLoadingCategories) {
      setRecipes([]);
      setPage(1);
      setSelectedRecipes(new Set());
      fetchRecipes(1, searchQuery, selectedCategory);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory]);

  // Handle search
  const handleSearch = () => {
    setRecipes([]);
    setPage(1);
    fetchRecipes(1, searchQuery, selectedCategory);
  };

  // Load more
  const handleLoadMore = () => {
    fetchRecipes(page + 1, searchQuery, selectedCategory);
  };

  // Refresh Payhip status for current recipes (without refetching all data)
  const refreshPayhipStatus = useCallback(async () => {
    if (recipes.length === 0) return;

    try {
      const urls = recipes.map(r => r.url).join(",");
      const checkRes = await fetch(`${DISHSNAP_API}/api/payhip/posts/check?urls=${encodeURIComponent(urls)}`, {
        headers: authHeaders(),
      });
      const checkData = await checkRes.json() as {
        posted_urls?: string[];
        url_to_payhip?: Record<string, string | null>;
        url_to_pin?: Record<string, string | null>;
        url_to_pin_title?: Record<string, string | null>;
        url_to_pinterest_title?: Record<string, string | null>;
        url_to_pinterest_desc?: Record<string, string | null>;
        url_to_metricool_scheduled?: Record<string, boolean>;
        url_to_payhip_account?: Record<string, string | null>;
      };

      if (checkData.posted_urls && checkData.url_to_payhip) {
        const postedSet = new Set(checkData.posted_urls);
        setRecipes(prev => prev.map(recipe => {
          if (postedSet.has(recipe.url)) {
            return {
              ...recipe,
              payhipPosted: true,
              payhipUrl: checkData.url_to_payhip?.[recipe.url] || recipe.payhipUrl,
              pinUrl: checkData.url_to_pin?.[recipe.url] || recipe.pinUrl,
              pinTitle: checkData.url_to_pin_title?.[recipe.url] || recipe.pinTitle,
              pinterestTitle: checkData.url_to_pinterest_title?.[recipe.url] || recipe.pinterestTitle,
              pinterestDescription: checkData.url_to_pinterest_desc?.[recipe.url] || recipe.pinterestDescription,
              metricoolScheduled: checkData.url_to_metricool_scheduled?.[recipe.url] || recipe.metricoolScheduled,
              payhipAccountId: checkData.url_to_payhip_account?.[recipe.url] || recipe.payhipAccountId,
            };
          }
          return recipe;
        }));
      }
    } catch (e) {
      console.log("Failed to refresh Payhip status:", e);
    }
  }, [recipes]);

  // Keep ref updated with latest refreshPayhipStatus function
  useEffect(() => {
    refreshPayhipStatusRef.current = refreshPayhipStatus;
  }, [refreshPayhipStatus]);

  // Auto-load more when hideScheduled is enabled and we have few non-done items
  useEffect(() => {
    if (!hideScheduled || isLoading || !hasMore) return;

    const nonScheduledCount = recipes.filter(r => !r.metricoolScheduled).length;
    // If we have fewer than 10 non-scheduled items and there's more to load, auto-load
    if (nonScheduledCount < 10 && recipes.length > 0) {
      fetchRecipes(page + 1, searchQuery, selectedCategory);
    }
  }, [hideScheduled, recipes, isLoading, hasMore, page, searchQuery, selectedCategory, fetchRecipes]);

  // Save API token to database
  const handleSaveToken = async () => {
    try {
      const response = await fetch(apiUrl("/api/scraper-settings"), {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify({ apiToken }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        console.error("Failed to save API token:", response.status, errorText);
        alert(`Failed to save API token: ${response.status} - ${errorText}`);
        return;
      }
      setShowSettings(false);
      fetchCategories();
      fetchRecipes(1, "", selectedCategory);
    } catch (e) {
      console.error("Failed to save API token:", e);
      alert(`Failed to save API token: ${e}`);
    }
  };

  // Toggle recipe selection
  const handleToggleSelection = (id: number) => {
    setSelectedRecipes(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Select all (only non-done posts when hideScheduled is active)
  const handleSelectAll = () => {
    const selectableRecipes = hideScheduled
      ? recipes.filter(r => !r.metricoolScheduled)
      : recipes;

    const allSelected = selectableRecipes.every(r => selectedRecipes.has(r.id));

    if (allSelected && selectableRecipes.length > 0) {
      setSelectedRecipes(new Set());
    } else {
      setSelectedRecipes(new Set(selectableRecipes.map(r => r.id)));
    }
  };

  // Copy single recipe data
  const handleCopyRecipe = async (recipe: ScrapedRecipe) => {
    const data = {
      title: recipe.title,
      url: recipe.url,
      image: recipe.image,
      description: recipe.payhipDescription,
    };

    try {
      await navigator.clipboard.writeText(JSON.stringify(data, null, 2));
      setCopiedId(recipe.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  // Copy all selected recipes
  const handleCopySelected = async () => {
    const selectedData = recipes
      .filter(r => selectedRecipes.has(r.id))
      .map(recipe => ({
        title: recipe.title,
        url: recipe.url,
        image: recipe.image,
        description: recipe.payhipDescription,
      }));

    try {
      await navigator.clipboard.writeText(JSON.stringify(selectedData, null, 2));
      alert(`Copied ${selectedData.length} recipes to clipboard!`);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  // Export to CSV
  const handleExportCSV = () => {
    const dataToExport = selectedRecipes.size > 0
      ? recipes.filter(r => selectedRecipes.has(r.id))
      : recipes;

    const csvContent = [
      ["Title", "URL", "Image", "Description", "Category", "Created At"].join(","),
      ...dataToExport.map(r => [
        `"${r.title.replace(/"/g, '""')}"`,
        r.url,
        r.image,
        `"${r.firstParagraph.replace(/"/g, '""')}"`,
        r.category || "",
        r.createdAt,
      ].join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${selectedWebsite.id}_recipes_${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
  };

  // Get current selected blog name
  const getSelectedBlogName = (): string => {
    if (selectedPayhipAccount && payhipAccounts.length > 0) {
      const account = payhipAccounts.find(a => a.id === selectedPayhipAccount);
      if (account) return account.blogName;
    }
    // Fallback to default account
    const defaultAccount = payhipAccounts.find(a => a.isDefault);
    return defaultAccount?.blogName || "recipes";
  };

  // Post to Payhip using Chrome extension
  const handlePostToPayhip = async (recipe: ScrapedRecipe) => {
    const currentExtensionId = getSelectedAccountExtensionId();
    const account = payhipAccounts.find(a => a.id === selectedPayhipAccount);

    console.log("[Queue Debug] Selected account:", account?.name, "extensionId:", account?.extensionId, "currentExtensionId:", currentExtensionId);

    if (!extensionConnected || !currentExtensionId) {
      if (account && !account.extensionId) {
        alert(`No extension connected to "${account.name}". Install the Chrome extension and connect it to this account.\n\nGo to the extension popup and select "${account.name}" from the dropdown.`);
      } else {
        alert("Chrome extension not connected. Please install the DishSnap Payhip Poster extension.");
      }
      return;
    }

    if (typeof chrome === "undefined" || !chrome.runtime) {
      alert("Chrome extension API not available. Make sure you're using Chrome.");
      return;
    }

    const blogName = getSelectedBlogName();

    try {
      console.log("[Queue Debug] Sending to extensionId:", currentExtensionId, "blogName:", blogName);

      chrome.runtime.sendMessage(
        currentExtensionId,
        {
          action: "addToQueue",
          recipe: {
            title: recipe.title,
            description: recipe.payhipDescription,
            postUrl: recipe.url,
            imageUrl: recipe.image,
            blogName: blogName,
            recipeInfo: recipe.recipeInfo || null,
            userId: user?.id || null,
            payhipAccountId: selectedPayhipAccount, // Store account ID for Pinterest/Metricool
          },
        },
        (response: unknown) => {
          const resp = response as { success?: boolean } | undefined;
          if (resp?.success) {
            setPostedRecipes(prev => new Set([...prev, recipe.id]));
            checkExtensionConnection(); // Update queue count
            const accountName = payhipAccounts.find(a => a.id === selectedPayhipAccount)?.name || blogName;
            alert(`Added "${recipe.title}" to ${accountName} queue.\n\nExtension: ${currentExtensionId.substring(0, 8)}...\nBlog: ${blogName}\n\nOpen the extension popup and click "Start Posting" to post.`);
          } else {
            alert("Failed to add to queue. Make sure the extension is installed and connected.");
          }
        }
      );
    } catch (err) {
      alert(`Failed to communicate with extension: ${err instanceof Error ? err.message : "Unknown error"}`);
    }
  };

  // Bulk post selected recipes to Payhip via Chrome extension
  const handleBulkPostToPayhip = async () => {
    if (selectedRecipes.size === 0) {
      alert("Please select recipes to post");
      return;
    }

    const currentExtensionId = getSelectedAccountExtensionId();
    if (!extensionConnected || !currentExtensionId) {
      const account = payhipAccounts.find(a => a.id === selectedPayhipAccount);
      if (account && !account.extensionId) {
        alert(`No extension connected to "${account.name}". Install the Chrome extension and connect it to this account.`);
      } else {
        alert("Chrome extension not connected. Please install the DishSnap Payhip Poster extension.");
      }
      return;
    }

    if (typeof chrome === "undefined" || !chrome.runtime) {
      alert("Chrome extension API not available. Make sure you're using Chrome.");
      return;
    }

    // Filter out already-posted recipes
    const recipesToPost = recipes.filter(r => selectedRecipes.has(r.id) && !r.payhipPosted);

    if (recipesToPost.length === 0) {
      alert("All selected recipes have already been posted to Payhip");
      return;
    }

    const blogName = getSelectedBlogName();
    const accountName = payhipAccounts.find(a => a.id === selectedPayhipAccount)?.name || blogName;

    if (!confirm(`Add ${recipesToPost.length} recipes to "${accountName}" queue? (${selectedRecipes.size - recipesToPost.length} already posted will be skipped)`)) return;
    let successCount = 0;
    let failedCount = 0;

    // Helper function to delay between messages
    const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

    for (const recipe of recipesToPost) {
      try {
        await new Promise<void>((resolve, reject) => {
          if (!chrome?.runtime) {
            reject(new Error("Chrome not available"));
            return;
          }

          // Add timeout to prevent hanging
          const timeout = setTimeout(() => {
            reject(new Error("Timeout"));
          }, 5000);

          chrome.runtime.sendMessage(
            currentExtensionId,
            {
              action: "addToQueue",
              recipe: {
                title: recipe.title,
                description: recipe.payhipDescription,
                postUrl: recipe.url,
                imageUrl: recipe.image,
                blogName: blogName,
                recipeInfo: recipe.recipeInfo || null,
                userId: user?.id || null,
                payhipAccountId: selectedPayhipAccount, // Store account ID for Pinterest/Metricool
              },
            },
            (response: unknown) => {
              clearTimeout(timeout);
              const resp = response as { success?: boolean } | undefined;
              if (resp?.success) {
                successCount++;
                setPostedRecipes(prev => new Set([...prev, recipe.id]));
                resolve();
              } else {
                reject(new Error("Failed"));
              }
            }
          );
        });

        // Add 150ms delay between messages to prevent overwhelming the extension
        await delay(150);
      } catch {
        failedCount++;
        // Continue with next recipe
      }
    }

    setSelectedRecipes(new Set());
    checkExtensionConnection(); // Update queue count

    if (failedCount > 0) {
      alert(`Added ${successCount} recipes to "${accountName}" queue.\n${failedCount} failed to add.\n\nOpen the extension popup and click "Start Posting" to begin posting.`);
    } else {
      alert(`Added ${successCount} recipes to "${accountName}" queue.\n\nOpen the extension popup and click "Start Posting" to begin posting.`);
    }
  };

  // Track a post as done (mark as posted to Payhip)
  const handleMarkAsPosted = async (recipe: ScrapedRecipe) => {
    const payhipUrl = prompt("Enter the Payhip blog post URL (or leave empty to just mark as posted):");
    if (payhipUrl === null) return; // User cancelled

    try {
      const response = await fetch(`${DISHSNAP_API}/api/payhip/posts/track`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({
          source_url: recipe.url,
          payhip_url: payhipUrl || null,
          title: recipe.title,
        }),
      });

      if (!response.ok) {
        const error = await response.text();
        alert(`Failed to track post: ${error}`);
        return;
      }

      // Update local state
      setRecipes(prev => prev.map(r =>
        r.id === recipe.id
          ? { ...r, payhipPosted: true, payhipUrl: payhipUrl || undefined }
          : r
      ));
      setPostedRecipes(prev => {
        const next = new Set(prev);
        next.delete(recipe.id);
        return next;
      });
      alert("Post tracked successfully!");
    } catch (e) {
      console.error("Failed to track post:", e);
      alert(`Failed to track post: ${e}`);
    }
  };

  // Save extension ID to database
  const handleSaveExtensionId = async () => {
    try {
      const response = await fetch(apiUrl("/api/scraper-settings"), {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify({ extensionId }),
      });
      if (!response.ok) {
        const error = await response.json();
        console.error("Failed to save extension ID:", error);
        alert("Failed to save extension ID");
        return;
      }
      checkExtensionConnection();
    } catch (e) {
      console.error("Failed to save extension ID:", e);
      alert("Failed to save extension ID");
    }
  };

  // Get templates for a specific account ID (falls back to selected account)
  const getTemplatesForAccount = (accountId?: string): string[] => {
    const id = accountId || selectedPayhipAccount;
    const account = payhipAccounts.find(a => a.id === id);
    return account?.templates ?? AVAILABLE_TEMPLATES.map(t => t.id);
  };

  // Generate Pinterest pins for selected Payhip-posted recipes
  const handleGeneratePins = async () => {
    // Get selected recipes that are Payhip-posted, have AI Meta (pinTitle), and DON'T already have pins
    const recipesToGenerate = recipes.filter(
      r => selectedRecipes.has(r.id) && r.payhipPosted && r.image && r.pinTitle && !r.pinUrl
    );
    const alreadyHavePins = recipes.filter(
      r => selectedRecipes.has(r.id) && r.pinUrl
    ).length;
    const needAIMeta = recipes.filter(
      r => selectedRecipes.has(r.id) && r.payhipPosted && r.image && !r.pinTitle && !r.pinUrl
    ).length;

    if (recipesToGenerate.length === 0) {
      if (alreadyHavePins > 0) {
        alert(`All ${alreadyHavePins} selected recipes already have Pinterest pins`);
      } else if (needAIMeta > 0) {
        alert(`${needAIMeta} recipes need AI Meta first! Run "AI Meta" before generating pins.`);
      } else {
        alert("Please select Payhip-posted recipes to generate pins for");
      }
      return;
    }

    // Check if recipes use different accounts - will use each recipe's original account
    const accountIds = [...new Set(recipesToGenerate.map(r => r.payhipAccountId).filter(Boolean))];
    const usingMultipleAccounts = accountIds.length > 1;
    const accountsInfo = usingMultipleAccounts
      ? `Using original account templates for each recipe (${accountIds.length} accounts)`
      : `Using ${payhipAccounts.find(a => a.id === (accountIds[0] || selectedPayhipAccount))?.name || "Default"}'s templates`;

    const skipMsg = alreadyHavePins > 0 ? ` (${alreadyHavePins} already have pins - will be skipped)` : "";

    if (!confirm(`Generate Pinterest pins for ${recipesToGenerate.length} recipes?\n\n${accountsInfo}${skipMsg}\n\nThis may take a few minutes.`)) {
      return;
    }

    setIsGeneratingPins(true);

    try {
      // Use Modal's parallel endpoint for maximum speed
      let successCount = 0;
      const newPins = new Map(generatedPins);
      const pinsToSave: Array<{ source_url: string; pin_url: string; featured_image?: string }> = [];

      // Prepare all pin requests - use pinTitle (short AI-generated title) instead of full title
      const pinRequests = recipesToGenerate.map(recipe => {
        const recipeAccount = payhipAccounts.find(a => a.id === (recipe.payhipAccountId || selectedPayhipAccount));
        const recipeAccountTemplates = getTemplatesForAccount(recipe.payhipAccountId);
        const randomTemplate = recipeAccountTemplates[Math.floor(Math.random() * recipeAccountTemplates.length)];
        const templateCategory = randomTemplate?.split("-")[0] || "KK";
        const titleFontSize = recipeAccount?.titleFontSizes?.[templateCategory] || 45;

        return {
          title: recipe.pinTitle!, // Use short AI-generated title for pin overlay
          image_url: recipe.image,
          template: randomTemplate,
          title_font_size: titleFontSize,
          _recipe: recipe, // Keep reference for mapping results
        };
      });

      // Send all to Modal's parallel endpoint
      const pinterestApiUrl = `https://${modalAccountPrefix}pinterest-pin-generator-web-app.modal.run`;
      const response = await fetch(`${pinterestApiUrl}/generate-multiple`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          pins: pinRequests.map(({ _recipe, ...rest }) => rest),
        }),
      });

      if (response.ok) {
        const data = await response.json();
        const generatedPinsData = data.pins || [];

        // Map results back to recipes by title
        for (const pinData of generatedPinsData) {
          const matchingRequest = pinRequests.find(r => r.title === pinData.title);
          if (matchingRequest && pinData.pin_url) {
            const recipe = matchingRequest._recipe;
            newPins.set(recipe.id, pinData.pin_url);
            pinsToSave.push({
              source_url: recipe.url,
              pin_url: pinData.pin_url,
              featured_image: recipe.image,
            });
            successCount++;
          }
        }
      }

      // Save pin URLs to database
      if (pinsToSave.length > 0) {
        try {
          await fetch(`${DISHSNAP_API}/api/payhip/posts/bulk-pin`, {
            method: "POST",
            headers: authHeaders(),
            body: JSON.stringify({ pins: pinsToSave }),
          });
          // Update local state with pin URLs
          setRecipes(prev => prev.map(r => {
            const savedPin = pinsToSave.find(p => p.source_url === r.url);
            if (savedPin) {
              return { ...r, pinUrl: savedPin.pin_url };
            }
            return r;
          }));
        } catch (e) {
          console.log("Failed to save pin URLs to database:", e);
        }
      }

      setGeneratedPins(newPins);
      alert(`Generated ${successCount}/${recipesToGenerate.length} Pinterest pins!`);

    } catch (err) {
      alert(`Error: ${err instanceof Error ? err.message : "Unknown error"}`);
    } finally {
      setIsGeneratingPins(false);
    }
  };

  // State for showing pin results
  const [pinResults, setPinResults] = useState<Array<{title: string, pin_url: string}>>([]);
  const [showPinResults, setShowPinResults] = useState(false);

  // Generate all Payhip pins via batch endpoint
  const handleGenerateAllPayhipPins = async () => {
    if (!confirm("Generate Pinterest pins for ALL Payhip-posted recipes? This may take several minutes.")) {
      return;
    }

    setIsGeneratingPins(true);
    setPinResults([]);
    setShowPinResults(false);

    try {
      const pinterestApiUrl = `https://${modalAccountPrefix}pinterest-pin-generator-web-app.modal.run`;
      const response = await fetch(`${pinterestApiUrl}/generate-payhip-pins?limit=50`);

      if (!response.ok) {
        throw new Error(`Failed: ${response.status}`);
      }

      const data = await response.json();
      const pins = data.pins || [] as Array<{ title: string; pin_url: string; source_url?: string; featured_image?: string }>;

      // Store results and show them
      setPinResults(pins);
      setShowPinResults(true);

      // Save pin URLs to database
      if (pins.length > 0) {
        const pinsToSave = pins
          .filter((p: { source_url?: string; pin_url: string }) => p.source_url && p.pin_url)
          .map((p: { source_url?: string; pin_url: string; featured_image?: string }) => ({
            source_url: p.source_url!,
            pin_url: p.pin_url,
            featured_image: p.featured_image,
          }));

        if (pinsToSave.length > 0) {
          try {
            await fetch(`${DISHSNAP_API}/api/payhip/posts/bulk-pin`, {
              method: "POST",
              headers: authHeaders(),
              body: JSON.stringify({ pins: pinsToSave }),
            });
            console.log(`Saved ${pinsToSave.length} pin URLs to database`);
          } catch (e) {
            console.log("Failed to save pin URLs to database:", e);
          }
        }
      }

      console.log("Generated Pinterest Pins:", pins);

    } catch (err) {
      alert(`Error: ${err instanceof Error ? err.message : "Unknown error"}`);
    } finally {
      setIsGeneratingPins(false);
    }
  };

  // Copy all pin URLs to clipboard
  const handleCopyPinUrls = async () => {
    const urls = pinResults.map(p => p.pin_url).join("\n");
    try {
      await navigator.clipboard.writeText(urls);
      alert("Pin URLs copied to clipboard!");
    } catch {
      // Fallback - show in alert
      alert("URLs:\n" + urls);
    }
  };

  // Generate Pinterest metadata (short pin title + full title + description) for Payhip-posted recipes
  const handleGeneratePinterestMetadata = async () => {
    // Get selected Payhip-posted recipes that DON'T already have metadata
    const recipesToGenerate = recipes.filter(
      r => selectedRecipes.has(r.id) && r.payhipPosted && !r.pinTitle
    );
    const alreadyHaveMetadata = recipes.filter(
      r => selectedRecipes.has(r.id) && r.pinTitle
    ).length;

    if (recipesToGenerate.length === 0) {
      if (alreadyHaveMetadata > 0) {
        alert(`All ${alreadyHaveMetadata} selected recipes already have AI metadata`);
      } else {
        alert("Please select Payhip-posted recipes to generate AI metadata for");
      }
      return;
    }

    const skipMsg = alreadyHaveMetadata > 0 ? ` (${alreadyHaveMetadata} already have metadata - will be skipped)` : "";
    if (!confirm(`Generate AI metadata for ${recipesToGenerate.length} recipes?\n\nThis includes:\n• Short pin title (for image overlay)\n• Pinterest title & description (for Metricool)${skipMsg}`)) {
      return;
    }

    setIsGeneratingMetadata(true);

    try {
      const sourceUrls = recipesToGenerate.map(r => r.url);
      const BATCH_SIZE = 20; // API limit is 20 per request
      const batches = [];

      // Split into batches of 20
      for (let i = 0; i < sourceUrls.length; i += BATCH_SIZE) {
        batches.push(sourceUrls.slice(i, i + BATCH_SIZE));
      }

      let totalGenerated = 0;
      const allResults: Array<{
        source_url: string;
        pin_title: string;
        pinterest_title: string;
        pinterest_description: string;
      }> = [];

      // Process each batch
      for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
        const batch = batches[batchIndex]!;
        console.log(`Processing batch ${batchIndex + 1}/${batches.length} (${batch.length} recipes)`);

        const response = await fetch(`${DISHSNAP_API}/api/payhip/posts/generate-pinterest-metadata`, {
          method: "POST",
          headers: authHeaders(),
          body: JSON.stringify({ source_urls: batch }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error((errorData as { error?: string }).error || `Failed: ${response.status}`);
        }

        const data = await response.json() as {
          success: boolean;
          generated: number;
          total: number;
          results: Array<{
            source_url: string;
            pin_title: string;
            pinterest_title: string;
            pinterest_description: string;
          }>;
        };

        totalGenerated += data.generated;
        allResults.push(...data.results);

        // Update local state after each batch so user sees progress
        setRecipes(prev => prev.map(r => {
          const result = data.results.find(res => res.source_url === r.url);
          if (result) {
            return {
              ...r,
              pinTitle: result.pin_title,
              pinterestTitle: result.pinterest_title,
              pinterestDescription: result.pinterest_description,
            };
          }
          return r;
        }));

        // Small delay between batches to avoid overwhelming the API
        if (batchIndex < batches.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }

      alert(`Generated Pinterest metadata for ${totalGenerated}/${sourceUrls.length} recipes!`);

    } catch (err) {
      alert(`Error: ${err instanceof Error ? err.message : "Unknown error"}`);
    } finally {
      setIsGeneratingMetadata(false);
    }
  };

  // Add new Metricool account
  const handleAddMetricoolAccount = async () => {
    if (!newMetricoolName.trim() || !newMetricoolBrand.trim()) {
      alert("Please fill in account name and Pinterest brand name");
      return;
    }

    if (!newMetricoolKeyName) {
      alert("Please select a Metricool API key");
      return;
    }

    try {
      const boards = newMetricoolBoards.split(",").map(b => b.trim()).filter(Boolean);
      const response = await fetch(`${DISHSNAP_API}/api/metricool/accounts`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({
          name: newMetricoolName.trim(),
          pinterest_brand_name: newMetricoolBrand.trim(),
          pinterest_boards: boards,
          custom_link: newMetricoolLink.trim() || "",
          metricool_key_name: newMetricoolKeyName,
          is_default: metricoolAccounts.length === 0,
        }),
      });

      if (response.ok) {
        const data = await response.json() as { account: MetricoolAccount };
        setMetricoolAccounts([...metricoolAccounts, data.account]);
        if (!selectedMetricoolAccount) {
          setSelectedMetricoolAccount(data.account.id);
        }
        setNewMetricoolName("");
        setNewMetricoolBrand("");
        setNewMetricoolBoards("");
        setNewMetricoolLink("");
        setNewMetricoolKeyName("");
        alert("Metricool account added!");
      } else {
        const err = await response.json() as { error?: string };
        alert(err.error || "Failed to add account");
      }
    } catch (e) {
      alert("Failed to add Metricool account");
    }
  };

  // Delete Metricool account
  const handleDeleteMetricoolAccount = async (accountId: number) => {
    const account = metricoolAccounts.find(a => a.id === accountId);
    if (!confirm(`Delete Metricool account "${account?.name}"?`)) return;

    try {
      const response = await fetch(`${DISHSNAP_API}/api/metricool/accounts/${accountId}`, {
        method: "DELETE",
      });

      if (response.ok) {
        const updated = metricoolAccounts.filter(a => a.id !== accountId);
        setMetricoolAccounts(updated);
        if (selectedMetricoolAccount === accountId && updated.length > 0) {
          setSelectedMetricoolAccount(updated[0]!.id);
        }
      }
    } catch (e) {
      alert("Failed to delete account");
    }
  };

  // Helper to generate dates with X pins per day starting from start date
  const getDatesByPinsPerDay = (startDate: string, pinsPerDay: number, count: number): string[] => {
    const start = new Date(startDate);
    const dates: string[] = [];

    // Group pins by day first
    const dayGroups: Map<number, number> = new Map(); // dayOffset -> count
    for (let i = 0; i < count; i++) {
      const dayOffset = Math.floor(i / pinsPerDay);
      dayGroups.set(dayOffset, (dayGroups.get(dayOffset) || 0) + 1);
    }

    // For each pin, assign a random time within its day
    // Spread pins throughout the day (8:00 AM to 10:00 PM = 14 hours)
    let pinIndex = 0;
    for (const [dayOffset, pinsOnDay] of dayGroups) {
      const date = new Date(start);
      date.setDate(date.getDate() + dayOffset);

      // Generate random times for this day's pins, spread throughout the day
      const times: string[] = [];
      for (let j = 0; j < pinsOnDay; j++) {
        // Random hour between 8 (8 AM) and 21 (9 PM)
        const hour = 8 + Math.floor(Math.random() * 14);
        // Random minute
        const minute = Math.floor(Math.random() * 60);
        times.push(`${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`);
      }

      // Sort times so they're in chronological order within the day
      times.sort();

      // Add each datetime to the result
      for (const time of times) {
        const dateStr = date.toISOString().split('T')[0];
        dates.push(`${dateStr}T${time}`);
        pinIndex++;
      }
    }

    return dates;
  };

  // Calculate finish date based on pins per day
  const calculateFinishDate = (startDate: string, pinsPerDay: number, count: number): string => {
    const totalDays = Math.ceil(count / pinsPerDay) - 1;
    const start = new Date(startDate);
    start.setDate(start.getDate() + totalDays);
    return start.toISOString().split('T')[0] as string;
  };

  // Get account settings for a specific recipe (uses recipe's stored account or falls back to selected)
  const getAccountForRecipe = (recipe: ScrapedRecipe): PayhipAccount | undefined => {
    // First try recipe's stored account
    if (recipe.payhipAccountId) {
      const recipeAccount = payhipAccounts.find(a => a.id === recipe.payhipAccountId);
      if (recipeAccount) return recipeAccount;
    }
    // Fallback to selected account
    return payhipAccounts.find(a => a.id === selectedPayhipAccount);
  };

  // Schedule selected recipes to Metricool
  const handleScheduleToMetricool = async () => {
    // Get selected recipes that have pins and AI metadata
    const recipesToSchedule = recipes.filter(
      r => selectedRecipes.has(r.id) && r.pinUrl && r.pinterestTitle
    );

    if (recipesToSchedule.length === 0) {
      alert("Please select recipes that have Pinterest pins AND AI metadata generated");
      return;
    }

    // Group recipes by their account and validate each account has required settings
    const recipesByAccount = new Map<string, ScrapedRecipe[]>();
    const missingSettings: string[] = [];

    for (const recipe of recipesToSchedule) {
      const account = getAccountForRecipe(recipe);
      if (!account) {
        missingSettings.push(`"${recipe.title}" - No account found`);
        continue;
      }

      // Validate account has required Metricool settings
      if (!account.metricoolKeyName) {
        missingSettings.push(`"${recipe.title}" - ${account.name}: No Metricool API key`);
        continue;
      }
      if (!account.pinterestBrandName) {
        missingSettings.push(`"${recipe.title}" - ${account.name}: No Pinterest Brand`);
        continue;
      }
      if (!account.pinterestBoards) {
        missingSettings.push(`"${recipe.title}" - ${account.name}: No Pinterest Boards`);
        continue;
      }

      const accountId = account.id;
      if (!recipesByAccount.has(accountId)) {
        recipesByAccount.set(accountId, []);
      }
      recipesByAccount.get(accountId)!.push(recipe);
    }

    if (missingSettings.length > 0) {
      const maxShow = 5;
      const shown = missingSettings.slice(0, maxShow);
      const remaining = missingSettings.length - maxShow;
      alert(`Some recipes are missing Metricool settings:\n\n${shown.join("\n")}${remaining > 0 ? `\n...and ${remaining} more` : ""}\n\nPlease configure Metricool settings in the account Settings.`);
      return;
    }

    // Build summary of accounts being used
    const accountSummary = Array.from(recipesByAccount.entries()).map(([accountId, recs]) => {
      const account = payhipAccounts.find(a => a.id === accountId);
      return `${account?.name}: ${recs.length} recipes`;
    }).join("\n");

    // Build schedule info for confirmation
    let scheduleInfo = "";
    let finishDate = "";
    if (useMultiDay && scheduleStartDate) {
      finishDate = calculateFinishDate(scheduleStartDate, pinsPerDay, recipesToSchedule.length);
      scheduleInfo = `\n\nSchedule: ${scheduleStartDate} → ${finishDate} (${pinsPerDay} pins/day)`;
    } else if (scheduleStartDate) {
      scheduleInfo = `\n\nSchedule date: ${scheduleStartDate}`;
      finishDate = scheduleStartDate;
    }

    if (!confirm(`Schedule ${recipesToSchedule.length} recipes to Metricool?\n\nAccounts:\n${accountSummary}${scheduleInfo}`)) {
      return;
    }

    setIsSchedulingMetricool(true);
    setScheduleProgress({ current: 0, total: recipesToSchedule.length, finishDate });

    try {
      // Generate schedule dates if multi-day is enabled
      let scheduleDates: string[] | undefined;
      if (useMultiDay && scheduleStartDate) {
        scheduleDates = getDatesByPinsPerDay(scheduleStartDate, pinsPerDay, recipesToSchedule.length);
      } else if (scheduleStartDate) {
        scheduleDates = recipesToSchedule.map(() => scheduleStartDate);
      }

      // Process in parallel batches of 3 (safe for Metricool API rate limits)
      const BATCH_SIZE = 3;
      let successCount = 0;
      const results: Array<{ source_url: string; success: boolean; board?: string; error?: string }> = [];

      for (let batchStart = 0; batchStart < recipesToSchedule.length; batchStart += BATCH_SIZE) {
        const batch = recipesToSchedule.slice(batchStart, batchStart + BATCH_SIZE);

        // Add delay between batches (2s to avoid rate limits)
        if (batchStart > 0) {
          await new Promise(resolve => setTimeout(resolve, 2000));
        }

        // Process batch in parallel
        const batchResults = await Promise.allSettled(
          batch.map(async (recipe, batchIndex) => {
            const i = batchStart + batchIndex;
            const scheduleDate = scheduleDates?.[i];

            // Get the account for THIS recipe (uses stored payhipAccountId)
            const recipeAccount = getAccountForRecipe(recipe);
            if (!recipeAccount) {
              return { source_url: recipe.url, success: false, error: "No account found" };
            }

            // Parse boards from this account's settings
            const boardsList = recipeAccount.pinterestBoards?.split(",").map(b => b.trim()).filter(Boolean) || [];

            const response = await fetch(`${DISHSNAP_API}/api/payhip/posts/schedule-metricool`, {
              method: "POST",
              headers: authHeaders(),
              body: JSON.stringify({
                source_urls: [recipe.url],
                metricool_key_name: recipeAccount.metricoolKeyName,
                pinterest_brand_name: recipeAccount.pinterestBrandName,
                pinterest_boards: boardsList,
                schedule_date: scheduleDate,
                payhip_account_id: recipeAccount.id,
              }),
            });

            const data = await response.json() as {
              success: boolean;
              results: Array<{ source_url: string; success: boolean; board?: string; error?: string }>;
            };

            if (data.success && data.results[0]) {
              return data.results[0];
            } else {
              return { source_url: recipe.url, success: false, error: data.results?.[0]?.error || "Unknown error" };
            }
          })
        );

        // Collect batch results
        for (const result of batchResults) {
          if (result.status === "fulfilled") {
            results.push(result.value);
            if (result.value.success) successCount++;
          } else {
            results.push({ source_url: "unknown", success: false, error: "Request failed" });
          }
        }

        setScheduleProgress({ current: Math.min(batchStart + BATCH_SIZE, recipesToSchedule.length), total: recipesToSchedule.length, finishDate });
      }

      const boardsUsed = [...new Set(results.filter(r => r.success && r.board).map(r => r.board))];
      alert(`Scheduled ${successCount}/${recipesToSchedule.length} recipes to Metricool!\n\nBoards: ${boardsUsed.join(", ")}`);
      setSelectedRecipes(new Set());
      setScheduleStartDate("");
      setUseMultiDay(false);
    } catch (err) {
      alert(`Error: ${err instanceof Error ? err.message : "Unknown error"}`);
    } finally {
      setIsSchedulingMetricool(false);
      setScheduleProgress(null);
    }
  };

  // Helper to update account in API
  const updateAccountInApi = async (accountId: string, updates: Partial<PayhipAccount>) => {
    try {
      await fetch(apiUrl(`/api/scraper-settings/payhip-accounts/${accountId}`), {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify(updates),
      });
    } catch (e) {
      console.error("Failed to update account:", e);
    }
  };

  // Add new Payhip account
  const handleAddAccount = async () => {
    if (!newAccountName.trim() || !newAccountBlog.trim()) {
      alert("Please fill in both account name and blog name");
      return;
    }
    try {
      const response = await fetch(apiUrl("/api/scraper-settings/payhip-accounts"), {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({
          name: newAccountName.trim(),
          blogName: newAccountBlog.trim(),
          isDefault: payhipAccounts.length === 0,
        }),
      });
      if (response.ok) {
        const data = await response.json() as { account: PayhipAccount };
        const updated = [...payhipAccounts, data.account];
        setPayhipAccounts(updated);
        setNewAccountName("");
        setNewAccountBlog("");
        if (data.account.isDefault) {
          setSelectedPayhipAccount(data.account.id);
        }
      }
    } catch (e) {
      console.error("Failed to add account:", e);
    }
  };

  // Set account as default
  const handleSetDefaultAccount = async (accountId: string) => {
    const updated = payhipAccounts.map(a => ({
      ...a,
      isDefault: a.id === accountId
    }));
    setPayhipAccounts(updated);
    await updateAccountInApi(accountId, { isDefault: true });
  };

  // Delete account
  const handleDeleteAccount = async (accountId: string) => {
    const account = payhipAccounts.find(a => a.id === accountId);
    if (!confirm(`Delete account "${account?.name}"?`)) return;

    try {
      await fetch(apiUrl(`/api/scraper-settings/payhip-accounts/${accountId}`), {
        method: "DELETE",
        headers: authHeaders(),
      });

      const wasDefault = account?.isDefault;
      let updated = payhipAccounts.filter(a => a.id !== accountId);

      // Set first remaining as default if we deleted the default
      if (wasDefault && updated.length > 0 && updated[0]) {
        updated = [{ ...updated[0], isDefault: true }, ...updated.slice(1)];
      }

      setPayhipAccounts(updated);

      // Update selected if we deleted the selected account
      if (selectedPayhipAccount === accountId && updated.length > 0 && updated[0]) {
        setSelectedPayhipAccount(updated[0].id);
      }
    } catch (e) {
      console.error("Failed to delete account:", e);
    }
  };

  // Toggle template for an account (deprecated - old hardcoded templates)
  const handleToggleAccountTemplate = async (accountId: string, templateId: string) => {
    const account = payhipAccounts.find(a => a.id === accountId);
    if (!account) return;

    // Get current templates (default to all if undefined)
    const currentTemplates = account.templates ?? AVAILABLE_TEMPLATES.map(t => t.id);

    let newTemplates: string[];
    if (currentTemplates.includes(templateId)) {
      // Don't allow removing the last template
      if (currentTemplates.length <= 1) {
        alert("You must have at least one template selected");
        return;
      }
      newTemplates = currentTemplates.filter(t => t !== templateId);
    } else {
      newTemplates = [...currentTemplates, templateId];
    }

    const updated = payhipAccounts.map(a =>
      a.id === accountId ? { ...a, templates: newTemplates } : a
    );
    setPayhipAccounts(updated);
    await updateAccountInApi(accountId, { templates: newTemplates });
  };

  // Toggle R2 template category for an account (deprecated - now using individual templates)
  const handleToggleTemplateCategory = async (accountId: string, category: string) => {
    const account = payhipAccounts.find(a => a.id === accountId);
    if (!account) return;

    // Get current categories (default to all main categories if undefined)
    const defaultCategories = ["KK", "VV", "YY", "ZZ"];
    const currentCategories = account.templateCategories ?? defaultCategories;

    let newCategories: string[];
    if (currentCategories.includes(category)) {
      // Don't allow removing the last category
      if (currentCategories.length <= 1) {
        alert("You must have at least one template category selected");
        return;
      }
      newCategories = currentCategories.filter(c => c !== category);
    } else {
      newCategories = [...currentCategories, category];
    }

    const updated = payhipAccounts.map(a =>
      a.id === accountId ? { ...a, templateCategories: newCategories } : a
    );
    setPayhipAccounts(updated);
    await updateAccountInApi(accountId, { templateCategories: newCategories });
  };

  // Select a specific template from a category (replaces any existing template from that category)
  const handleSelectTemplate = async (accountId: string, category: string, templateId: string) => {
    const account = payhipAccounts.find(a => a.id === accountId);
    if (!account) return;

    // Get current templates (use existing R2 templates field)
    const currentTemplates = account.templates || [];

    // Remove any existing template from this category
    let newTemplates = currentTemplates.filter(t => !t.startsWith(category + "-"));

    // Add new template if selected
    if (templateId) {
      newTemplates.push(templateId);
    }

    // Sort templates by category order
    const categoryOrder = ["KK", "VV", "YY", "ZZ"];
    newTemplates.sort((a, b) => {
      const catA = a.split("-")[0] || "";
      const catB = b.split("-")[0] || "";
      return categoryOrder.indexOf(catA) - categoryOrder.indexOf(catB);
    });

    const updated = payhipAccounts.map(a =>
      a.id === accountId ? { ...a, templates: newTemplates } : a
    );
    setPayhipAccounts(updated);
    await updateAccountInApi(accountId, { templates: newTemplates });
  };

  // Update Metricool API key for an account (deprecated)
  const handleUpdateMetricoolApiKey = async (accountId: string, apiKey: string) => {
    const updated = payhipAccounts.map(account => {
      if (account.id !== accountId) return account;
      return { ...account, metricoolApiKey: apiKey };
    });
    setPayhipAccounts(updated);
    // Note: metricoolApiKey is deprecated, not saving to API
  };

  // Update Pinterest brand name for an account
  const handleUpdatePinterestBrandName = async (accountId: string, brandName: string) => {
    const updated = payhipAccounts.map(account => {
      if (account.id !== accountId) return account;
      return { ...account, pinterestBrandName: brandName };
    });
    setPayhipAccounts(updated);
    await updateAccountInApi(accountId, { pinterestBrandName: brandName });
  };

  // Update Metricool key name for an account
  const handleUpdateMetricoolKeyName = async (accountId: string, keyName: string) => {
    const updated = payhipAccounts.map(account => {
      if (account.id !== accountId) return account;
      return { ...account, metricoolKeyName: keyName };
    });
    setPayhipAccounts(updated);
    await updateAccountInApi(accountId, { metricoolKeyName: keyName });
  };

  const handleUpdatePinterestBoards = async (accountId: string, boards: string) => {
    const updated = payhipAccounts.map(account => {
      if (account.id !== accountId) return account;
      return { ...account, pinterestBoards: boards };
    });
    setPayhipAccounts(updated);
    await updateAccountInApi(accountId, { pinterestBoards: boards });
  };

  // Update Extension ID for an account
  const handleUpdateExtensionId = async (accountId: string, extId: string) => {
    const updated = payhipAccounts.map(account => {
      if (account.id !== accountId) return account;
      return { ...account, extensionId: extId };
    });
    setPayhipAccounts(updated);
    await updateAccountInApi(accountId, { extensionId: extId });
  };

  // Update title font size for a category
  const handleUpdateTitleFontSize = async (accountId: string, category: string, fontSize: number) => {
    const account = payhipAccounts.find(a => a.id === accountId);
    if (!account) return;

    const currentSizes = account.titleFontSizes || {};
    const newTitleFontSizes = { ...currentSizes, [category]: fontSize };

    const updated = payhipAccounts.map(acc => {
      if (acc.id !== accountId) return acc;
      return { ...acc, titleFontSizes: newTitleFontSizes };
    });
    setPayhipAccounts(updated);

    // Use the computed value directly, not from state
    await updateAccountInApi(accountId, { titleFontSizes: newTitleFontSizes });
  };

  // Get templates for selected account (for pin generation)
  const getSelectedAccountTemplates = (): string[] => {
    const account = payhipAccounts.find(a => a.id === selectedPayhipAccount);
    // Return account templates or default to all templates
    return account?.templates ?? AVAILABLE_TEMPLATES.map(t => t.id);
  };

  // Add new website
  const handleAddWebsite = async () => {
    if (!newWebsiteName.trim() || !newWebsiteDomain.trim()) {
      alert("Please fill in both website name and domain");
      return;
    }
    // Clean domain (remove http/https and trailing slashes)
    let domain = newWebsiteDomain.trim()
      .replace(/^https?:\/\//, '')
      .replace(/\/$/, '');

    // Generate ID from name
    const id = newWebsiteName.trim().toLowerCase().replace(/[^a-z0-9]/g, '-');

    try {
      const response = await fetch(apiUrl("/api/scraper-settings/websites"), {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({
          id,
          name: newWebsiteName.trim(),
          domain,
        }),
      });
      if (response.ok) {
        const data = await response.json() as { website: Website };
        const updated = [...websites, data.website];
        setWebsitesState(updated);
        setNewWebsiteName("");
        setNewWebsiteDomain("");
      }
    } catch (e) {
      console.error("Failed to add website:", e);
    }
  };

  // Delete website
  const handleDeleteWebsite = async (siteId: string) => {
    const site = websites.find(w => w.id === siteId);
    if (!confirm(`Delete website "${site?.name}"?`)) return;

    try {
      await fetch(apiUrl(`/api/scraper-settings/websites/${siteId}`), {
        method: "DELETE",
        headers: authHeaders(),
      });

      const updated = websites.filter(w => w.id !== siteId);
      setWebsitesState(updated);

      // If we deleted the selected website, select first remaining
      if (selectedWebsite.id === siteId && updated.length > 0 && updated[0]) {
        setSelectedWebsite(updated[0]);
      }
    } catch (e) {
      console.error("Failed to delete website:", e);
    }
  };

  // Fetch Pinterest templates from R2 (limited to first 10 per category for performance)
  const fetchPinTemplates = useCallback(async () => {
    setIsLoadingTemplates(true);
    try {
      const response = await fetch(apiUrl("/api/templates"), {
        headers: authHeaders(),
      });
      if (response.ok) {
        const data = await response.json() as {
          templates: PinTemplate[];
          categories: string[];
          count: number;
        };
        // Limit to first 10 templates per category for performance
        const mainCategories = ["KK", "VV", "YY", "ZZ"];
        const limitedTemplates: PinTemplate[] = [];
        for (const cat of mainCategories) {
          const catTemplates = (data.templates || [])
            .filter(t => t.category === cat)
            .sort((a, b) => {
              const numA = parseInt(a.id.split("-")[1] || "0");
              const numB = parseInt(b.id.split("-")[1] || "0");
              return numA - numB;
            })
            .slice(0, 10); // Only first 10 per category
          limitedTemplates.push(...catTemplates);
        }
        setPinTemplates(limitedTemplates);
        setPinTemplateCategories(mainCategories);
      }
    } catch (e) {
      console.error("Failed to fetch templates:", e);
    } finally {
      setIsLoadingTemplates(false);
    }
  }, []);

  // NOTE: Removed auto-fetch of templates - using number inputs instead

  // Upload templates to R2
  const handleUploadTemplates = async () => {
    if (!templateUploadFiles || templateUploadFiles.length === 0) {
      alert("Please select HTML files to upload");
      return;
    }

    setIsUploadingTemplate(true);
    try {
      const formData = new FormData();
      for (let i = 0; i < templateUploadFiles.length; i++) {
        formData.append("files", templateUploadFiles[i]!);
      }
      // Add category for auto-numbering
      formData.append("category", templateUploadCategory);

      const response = await fetch(apiUrl("/api/templates/bulk-upload"), {
        method: "POST",
        headers: {
          "Authorization": authHeaders()["Authorization"] || "",
        },
        body: formData,
      });

      const data = await response.json() as { uploaded: number; total: number; results: Array<{ id: string; success: boolean; error?: string; originalName?: string }> };
      if (response.ok) {
        // Show which IDs were assigned
        const uploadedIds = data.results.filter(r => r.success).map(r => r.id).join(", ");
        alert(`Uploaded ${data.uploaded}/${data.total} templates!\nAssigned IDs: ${uploadedIds}`);
        setTemplateUploadFiles(null);
        // Reset file input
        const fileInput = document.getElementById("template-file-input") as HTMLInputElement;
        if (fileInput) fileInput.value = "";
        // Refresh templates list
        fetchPinTemplates();
      } else {
        alert(`Upload failed: ${JSON.stringify(data)}`);
      }
    } catch (e) {
      console.error("Failed to upload templates:", e);
      alert(`Error uploading templates: ${e instanceof Error ? e.message : "Unknown error"}`);
    } finally {
      setIsUploadingTemplate(false);
    }
  };

  // Delete a template from R2
  const handleDeleteTemplate = async (templateId: string) => {
    if (!confirm(`Delete template "${templateId}"?`)) return;

    try {
      const response = await fetch(apiUrl(`/api/templates/${templateId}`), {
        method: "DELETE",
        headers: authHeaders(),
      });

      if (response.ok) {
        setPinTemplates(prev => prev.filter(t => t.id !== templateId));
      } else {
        alert("Failed to delete template");
      }
    } catch (e) {
      console.error("Failed to delete template:", e);
    }
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <h1 className={styles.title}>Website Scraper</h1>
        <p className={styles.subtitle}>
          Fetch recipes from your websites - extract URL, image, and description
        </p>
      </div>

      {/* Settings Panel - Clean Minimal Design */}
      {showSettings && (
        <div style={{
          background: "white",
          borderRadius: "12px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          padding: "20px",
          marginBottom: "20px"
        }}>
          {/* Quick Settings Row */}
          <div style={{ display: "flex", gap: "20px", marginBottom: "20px", paddingBottom: "16px", borderBottom: "1px solid #eee" }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: "11px", color: "#666", textTransform: "uppercase", letterSpacing: "0.5px" }}>API Token</label>
              <div style={{ display: "flex", gap: "8px", marginTop: "6px" }}>
                <input type="password" value={apiToken} onChange={(e) => setApiTokenState(e.target.value)} placeholder="Enter token"
                  style={{ flex: 1, padding: "10px 12px", border: "1px solid #e0e0e0", borderRadius: "8px", fontSize: "13px" }} />
                <button onClick={handleSaveToken} style={{ padding: "10px 16px", background: "#333", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "13px" }}>Save</button>
              </div>
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: "11px", color: "#666", textTransform: "uppercase", letterSpacing: "0.5px", display: "flex", alignItems: "center", gap: "6px" }}>
                Extension
                <span style={{ fontSize: "9px", padding: "2px 6px", borderRadius: "4px", background: extensionConnected ? "#e8f5e9" : "#ffebee", color: extensionConnected ? "#2e7d32" : "#c62828" }}>
                  {extensionConnected ? "OK" : "OFF"}
                </span>
              </label>
              <div style={{ display: "flex", gap: "8px", marginTop: "6px" }}>
                <input type="text" value={extensionId} onChange={(e) => setExtensionId(e.target.value)} placeholder="Extension ID"
                  style={{ flex: 1, padding: "10px 12px", border: "1px solid #e0e0e0", borderRadius: "8px", fontSize: "13px" }} />
                <button onClick={handleSaveExtensionId} style={{ padding: "10px 16px", background: "#666", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "13px" }}>Save</button>
              </div>
            </div>
          </div>

          {/* Modal Account Prefix */}
          <div style={{ display: "flex", gap: "16px", marginBottom: "20px" }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: "11px", color: "#666", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Modal Account Prefix
              </label>
              <div style={{ display: "flex", gap: "8px", marginTop: "6px" }}>
                <input type="text" value={modalAccountPrefix} onChange={(e) => setModalAccountPrefix(e.target.value)} placeholder="digitixllc--"
                  style={{ flex: 1, padding: "10px 12px", border: "1px solid #e0e0e0", borderRadius: "8px", fontSize: "13px" }} />
                <button onClick={async () => {
                  try {
                    await fetch(apiUrl("/api/scraper-settings"), {
                      method: "PUT",
                      headers: authHeaders(),
                      body: JSON.stringify({ modalAccountPrefix }),
                    });
                  } catch (e) { console.error(e); }
                }} style={{ padding: "10px 16px", background: "#666", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "13px" }}>Save</button>
              </div>
              <div style={{ fontSize: "10px", color: "#999", marginTop: "4px" }}>
                e.g., digitixllc-- (include the double dash)
              </div>
            </div>
          </div>

          {/* Payhip Accounts */}
          <div style={{ marginBottom: "20px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
              <h4 style={{ margin: 0, fontSize: "14px", fontWeight: "600", color: "#333" }}>Payhip Accounts</h4>
              <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                <input type="text" value={newAccountName} onChange={(e) => setNewAccountName(e.target.value)} placeholder="Name"
                  style={{ padding: "8px 12px", border: "1px solid #e0e0e0", borderRadius: "6px", fontSize: "12px", width: "120px" }} />
                <input type="text" value={newAccountBlog} onChange={(e) => setNewAccountBlog(e.target.value)} placeholder="Blog"
                  style={{ padding: "8px 12px", border: "1px solid #e0e0e0", borderRadius: "6px", fontSize: "12px", width: "100px" }} />
                <button onClick={handleAddAccount} style={{ padding: "8px 14px", background: "#10b981", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "12px", fontWeight: "500" }}>Add</button>
              </div>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
              {payhipAccounts.map(account => (
                <div key={account.id} style={{
                  padding: "12px 16px",
                  background: account.isDefault ? "#f0fdf4" : "#fafafa",
                  border: account.isDefault ? "1px solid #86efac" : "1px solid #e5e5e5",
                  borderRadius: "10px",
                  minWidth: "280px",
                  flex: "1 1 280px",
                  maxWidth: "400px"
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "8px" }}>
                    <div>
                      <div style={{ fontWeight: "600", fontSize: "13px", color: "#333" }}>{account.name}</div>
                      <div style={{ fontSize: "11px", color: "#888" }}>Blog: {account.blogName} {account.isDefault && <span style={{ color: "#22c55e" }}>(Default)</span>}</div>
                    </div>
                    <div style={{ display: "flex", gap: "4px" }}>
                      {!account.isDefault && <button onClick={() => handleSetDefaultAccount(account.id)} style={{ padding: "4px 8px", fontSize: "10px", background: "#f3f4f6", color: "#374151", border: "none", borderRadius: "4px", cursor: "pointer" }}>Set Default</button>}
                      <button onClick={() => handleDeleteAccount(account.id)} style={{ padding: "4px 8px", fontSize: "10px", background: "#fef2f2", color: "#dc2626", border: "none", borderRadius: "4px", cursor: "pointer" }}>Delete</button>
                    </div>
                  </div>
                  {/* R2 Template Selection - Type template IDs */}
                  <div style={{ marginBottom: "10px" }}>
                    <div style={{ fontSize: "10px", color: "#888", marginBottom: "6px" }}>
                      Pin Templates (type template number, e.g., 1, 45, 100):
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                      {["KK", "VV", "YY", "ZZ"].map(cat => {
                        const selectedTemplate = (account.templates || []).find(t => t.startsWith(cat + "-"));
                        const currentNum = selectedTemplate ? selectedTemplate.split("-")[1] || "" : "";
                        const catColors: Record<string, string> = {
                          "KK": "#3b82f6", "VV": "#8b5cf6", "YY": "#f59e0b", "ZZ": "#10b981"
                        };
                        return (
                          <div key={cat} style={{ display: "flex", alignItems: "center", gap: "2px" }}>
                            <span style={{
                              fontSize: "11px",
                              fontWeight: "600",
                              color: catColors[cat] || "#6b7280",
                              padding: "4px 6px",
                              background: currentNum ? catColors[cat] : "#f3f4f6",
                              borderRadius: "4px 0 0 4px",
                              ...(currentNum ? { color: "white" } : {})
                            }}>{cat}-</span>
                            <input
                              type="number"
                              min="1"
                              max="200"
                              value={currentNum}
                              placeholder="#"
                              onChange={(e) => {
                                const num = e.target.value;
                                if (num && parseInt(num) > 0) {
                                  handleSelectTemplate(account.id, cat, `${cat}-${num}`);
                                } else {
                                  handleSelectTemplate(account.id, cat, "");
                                }
                              }}
                              style={{
                                width: "50px",
                                padding: "4px 6px",
                                fontSize: "11px",
                                border: `1px solid ${currentNum ? catColors[cat] : "#e0e0e0"}`,
                                borderRadius: "0 4px 4px 0",
                                background: currentNum ? "#f0f9ff" : "white",
                                textAlign: "center"
                              }}
                            />
                          </div>
                        );
                      })}
                    </div>
                    {(account.templates && account.templates.length > 0) && (
                      <div style={{ fontSize: "10px", color: "#10b981", marginTop: "6px" }}>
                        Using: {account.templates.join(", ")}
                      </div>
                    )}
                    {/* Title Font Sizes per Category */}
                    <div style={{ fontSize: "10px", color: "#888", marginTop: "8px", marginBottom: "4px" }}>
                      Title Font Size (px):
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                      {["KK", "VV", "YY", "ZZ"].map(cat => {
                        const currentSize = account.titleFontSizes?.[cat] || 45;
                        const catColors: Record<string, string> = {
                          "KK": "#3b82f6", "VV": "#8b5cf6", "YY": "#f59e0b", "ZZ": "#10b981"
                        };
                        return (
                          <div key={cat} style={{ display: "flex", alignItems: "center", gap: "2px" }}>
                            <span style={{
                              fontSize: "10px",
                              fontWeight: "600",
                              color: "white",
                              padding: "4px 6px",
                              background: catColors[cat],
                              borderRadius: "4px 0 0 4px"
                            }}>{cat}</span>
                            <input
                              type="number"
                              min="30"
                              max="120"
                              value={currentSize}
                              onChange={(e) => {
                                const size = parseInt(e.target.value);
                                if (size >= 30 && size <= 120) {
                                  handleUpdateTitleFontSize(account.id, cat, size);
                                }
                              }}
                              style={{
                                width: "45px",
                                padding: "4px 4px",
                                fontSize: "10px",
                                border: `1px solid ${catColors[cat]}`,
                                borderRadius: "0 4px 4px 0",
                                textAlign: "center"
                              }}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "8px", marginBottom: "8px" }}>
                    <input type="text" value={account.pinterestBrandName || ""} onChange={(e) => handleUpdatePinterestBrandName(account.id, e.target.value)} placeholder="Pinterest Brand"
                      style={{ flex: 1, padding: "8px 10px", fontSize: "12px", border: "1px solid #e0e0e0", borderRadius: "6px" }} />
                    <select value={account.metricoolKeyName || ""} onChange={(e) => handleUpdateMetricoolKeyName(account.id, e.target.value)}
                      style={{ padding: "8px 10px", fontSize: "12px", border: "1px solid #e0e0e0", borderRadius: "6px", background: "white", minWidth: "90px" }}>
                      <option value="">API Key</option>
                      {AVAILABLE_METRICOOL_KEYS.map(k => <option key={k} value={k}>{k.replace("METRICOOL_API_KEY", "Key")}</option>)}
                    </select>
                  </div>
                  <input type="text" value={account.pinterestBoards || ""} onChange={(e) => handleUpdatePinterestBoards(account.id, e.target.value)} placeholder="Boards (comma-separated: Food, Recipes, Dinner Ideas)"
                    style={{ width: "100%", padding: "8px 10px", fontSize: "12px", border: "1px solid #e0e0e0", borderRadius: "6px", marginBottom: "8px" }} />
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <input type="text" value={account.extensionId || ""} onChange={(e) => handleUpdateExtensionId(account.id, e.target.value)} placeholder="Chrome Extension ID"
                      style={{ flex: 1, padding: "8px 10px", fontSize: "12px", border: "1px solid #e0e0e0", borderRadius: "6px", fontFamily: "monospace" }} />
                    {account.extensionId && (
                      <span style={{ fontSize: "10px", color: "#22c55e", fontWeight: "500" }}>Connected</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Websites & Metricool Row */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
            {/* Websites */}
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                <h4 style={{ margin: 0, fontSize: "14px", fontWeight: "600", color: "#333" }}>Websites</h4>
                <div style={{ display: "flex", gap: "6px" }}>
                  <input type="text" value={newWebsiteName} onChange={(e) => setNewWebsiteName(e.target.value)} placeholder="Name" style={{ padding: "6px 10px", border: "1px solid #e0e0e0", borderRadius: "6px", fontSize: "12px", width: "80px" }} />
                  <input type="text" value={newWebsiteDomain} onChange={(e) => setNewWebsiteDomain(e.target.value)} placeholder="domain.com" style={{ padding: "6px 10px", border: "1px solid #e0e0e0", borderRadius: "6px", fontSize: "12px", width: "100px" }} />
                  <button onClick={handleAddWebsite} style={{ padding: "6px 12px", background: "#10b981", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "12px" }}>+</button>
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                {websites.map(site => (
                  <div key={site.id} style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px",
                    background: selectedWebsite.id === site.id ? "#eff6ff" : "#fafafa",
                    border: selectedWebsite.id === site.id ? "1px solid #93c5fd" : "1px solid #e5e5e5", borderRadius: "8px"
                  }}>
                    <div style={{ fontSize: "13px" }}><strong>{site.name}</strong> <span style={{ color: "#888" }}>{site.domain}</span></div>
                    <button onClick={() => handleDeleteWebsite(site.id)} style={{ padding: "4px 8px", fontSize: "10px", background: "#fef2f2", color: "#dc2626", border: "none", borderRadius: "4px", cursor: "pointer" }}>Delete</button>
                  </div>
                ))}
              </div>
            </div>

            {/* Metricool Accounts */}
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                <h4 style={{ margin: 0, fontSize: "14px", fontWeight: "600", color: "#333" }}>Metricool Accounts</h4>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "6px", marginBottom: "10px" }}>
                {metricoolAccounts.map(acc => (
                  <div key={acc.id} style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px",
                    background: acc.is_default ? "#f0fdf4" : "#fafafa",
                    border: acc.is_default ? "1px solid #86efac" : "1px solid #e5e5e5", borderRadius: "8px"
                  }}>
                    <div>
                      <div style={{ fontSize: "13px", fontWeight: "500" }}>{acc.name}</div>
                      <div style={{ fontSize: "11px", color: "#888" }}>{acc.pinterest_brand_name} | {acc.metricool_key_name?.replace("METRICOOL_API_KEY", "Key") || "No key"}</div>
                    </div>
                    <button onClick={() => handleDeleteMetricoolAccount(acc.id)} style={{ padding: "4px 8px", fontSize: "10px", background: "#fef2f2", color: "#dc2626", border: "none", borderRadius: "4px", cursor: "pointer" }}>Delete</button>
                  </div>
                ))}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px" }}>
                <input type="text" value={newMetricoolName} onChange={(e) => setNewMetricoolName(e.target.value)} placeholder="Name" style={{ padding: "8px 10px", border: "1px solid #e0e0e0", borderRadius: "6px", fontSize: "12px" }} />
                <input type="text" value={newMetricoolBrand} onChange={(e) => setNewMetricoolBrand(e.target.value)} placeholder="Brand" style={{ padding: "8px 10px", border: "1px solid #e0e0e0", borderRadius: "6px", fontSize: "12px" }} />
                <input type="text" value={newMetricoolBoards} onChange={(e) => setNewMetricoolBoards(e.target.value)} placeholder="Boards" style={{ padding: "8px 10px", border: "1px solid #e0e0e0", borderRadius: "6px", fontSize: "12px" }} />
                <div style={{ display: "flex", gap: "6px" }}>
                  <select value={newMetricoolKeyName} onChange={(e) => setNewMetricoolKeyName(e.target.value)} style={{ flex: 1, padding: "8px 10px", border: "1px solid #e0e0e0", borderRadius: "6px", fontSize: "12px", background: "white" }}>
                    <option value="">Key</option>
                    {AVAILABLE_METRICOOL_KEYS.map(k => <option key={k} value={k}>{k.replace("METRICOOL_API_KEY", "K")}</option>)}
                  </select>
                  <button onClick={handleAddMetricoolAccount} style={{ padding: "8px 14px", background: "#10b981", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "12px" }}>Add</button>
                </div>
              </div>
            </div>
          </div>

          {/* Pinterest Templates Section */}
          <div style={{ marginTop: "20px", paddingTop: "20px", borderTop: "1px solid #eee" }}>
            <h4 style={{ margin: "0 0 12px 0", fontSize: "14px", fontWeight: "600", color: "#333" }}>
              Pinterest Templates (R2)
            </h4>
            <p style={{ fontSize: "12px", color: "#666", marginBottom: "12px" }}>
              Templates are stored in R2. Use KK-#, VV-#, YY-#, ZZ-# format in account settings above.
            </p>

            {/* Upload New Templates */}
            <div style={{ display: "flex", gap: "10px", alignItems: "center", padding: "12px", background: "#fafafa", borderRadius: "8px", flexWrap: "wrap" }}>
              <select
                value={templateUploadCategory}
                onChange={(e) => setTemplateUploadCategory(e.target.value)}
                style={{
                  padding: "8px 12px",
                  fontSize: "13px",
                  borderRadius: "6px",
                  border: "1px solid #ddd",
                  background: "white",
                  fontWeight: "500"
                }}
              >
                <option value="KK">KK (Category 1)</option>
                <option value="VV">VV (Category 2)</option>
                <option value="YY">YY (Category 3)</option>
                <option value="ZZ">ZZ (Category 4)</option>
              </select>
              <input
                id="template-file-input"
                type="file"
                multiple
                accept=".html"
                onChange={(e) => setTemplateUploadFiles(e.target.files)}
                style={{ flex: 1, fontSize: "13px", minWidth: "150px" }}
              />
              <button
                onClick={handleUploadTemplates}
                disabled={isUploadingTemplate || !templateUploadFiles}
                style={{
                  padding: "8px 16px",
                  background: isUploadingTemplate ? "#9ca3af" : "#10b981",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: isUploadingTemplate ? "wait" : "pointer",
                  fontSize: "12px",
                  fontWeight: "500"
                }}
              >
                {isUploadingTemplate ? "Uploading..." : `Upload ${templateUploadFiles?.length || 0} to ${templateUploadCategory}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className={styles.controls}>
        <div className={styles.websiteSelector}>
          <label className={styles.label}>Website:</label>
          <select
            value={selectedWebsite.id}
            onChange={(e) => {
              const site = websites.find(w => w.id === e.target.value);
              if (site) {
                setSelectedWebsite(site);
                setRecipes([]);
                setPage(1);
              }
            }}
            className={styles.select}
          >
            {websites.map(site => (
              <option key={site.id} value={site.id}>
                {site.name} ({site.domain})
              </option>
            ))}
          </select>
        </div>

        <div className={styles.searchBox}>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            placeholder="Search recipes..."
            className={styles.searchInput}
          />
          <button onClick={handleSearch} className={styles.searchBtn}>
            Search
          </button>
        </div>

        {/* Category Filter Dropdown */}
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            disabled={isLoadingCategories || categories.length === 0}
            style={{
              padding: "8px 12px",
              borderRadius: "6px",
              border: "1px solid #ddd",
              background: selectedCategory ? "#e0f2fe" : "white",
              color: "#333",
              fontSize: "14px",
              minWidth: "150px",
              cursor: isLoadingCategories ? "wait" : "pointer"
            }}
          >
            <option value="">All Categories</option>
            {categories.map(cat => (
              <option key={cat.slug} value={cat.slug}>
                {cat.name}
              </option>
            ))}
          </select>
          {selectedCategory && (
            <button
              onClick={() => setSelectedCategory("")}
              style={{
                padding: "6px 10px",
                background: "#ef4444",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "12px"
              }}
              title="Clear category filter"
            >
              ✕
            </button>
          )}
        </div>

        <button
          onClick={() => setShowSettings(!showSettings)}
          className={styles.settingsBtn}
          title="Settings (API & Extension)"
          style={{
            padding: "8px 12px",
            background: showSettings ? "#0066cc" : "#f0f0f0",
            color: showSettings ? "white" : "#333",
            borderRadius: "6px",
            border: "1px solid #ddd"
          }}
        >
          ⚙️ Settings
        </button>

        {/* Payhip Only Filter */}
        <button
          onClick={() => setShowPayhipOnly(!showPayhipOnly)}
          style={{
            padding: "8px 12px",
            background: showPayhipOnly ? "#f97316" : "#f0f0f0",
            color: showPayhipOnly ? "white" : "#333",
            borderRadius: "6px",
            border: "1px solid #ddd",
            cursor: "pointer",
            fontWeight: showPayhipOnly ? "bold" : "normal"
          }}
          title="Show only Payhip-posted recipes"
        >
          {showPayhipOnly ? "Payhip Only" : "Show All"}
        </button>
        <button
          onClick={() => setHideScheduled(!hideScheduled)}
          style={{
            padding: "8px 12px",
            background: hideScheduled ? "#4caf50" : "#f0f0f0",
            color: hideScheduled ? "white" : "#333",
            borderRadius: "6px",
            border: "1px solid #ddd",
            cursor: "pointer",
            fontWeight: hideScheduled ? "bold" : "normal"
          }}
          title="Hide posts already scheduled to Metricool"
        >
          {hideScheduled ? "Hide Done" : "Show Done"}
        </button>
      </div>

      {/* Actions Bar */}
      {recipes.length > 0 && (
        <div className={styles.actionsBar}>
          <div className={styles.actionsLeft}>
            <button onClick={handleSelectAll} className={styles.selectAllBtn}>
              {(() => {
                const selectableRecipes = hideScheduled
                  ? recipes.filter(r => !r.metricoolScheduled)
                  : recipes;
                const allSelected = selectableRecipes.every(r => selectedRecipes.has(r.id)) && selectableRecipes.length > 0;
                return allSelected ? "Deselect All" : "Select All";
              })()}
            </button>
            <span className={styles.selectedCount}>
              {selectedRecipes.size > 0 && `${selectedRecipes.size} selected`}
            </span>
          </div>
          <div className={styles.actionsRight}>
            {/* Extension Status */}
            {extensionConnected && (
              <span style={{
                padding: "6px 12px",
                borderRadius: "4px",
                fontSize: "12px",
                background: "#d4edda",
                color: "#155724",
                display: "flex",
                alignItems: "center",
                gap: "6px"
              }}>
                Extension: {extensionQueueCount} in queue
              </span>
            )}
            {/* Payhip Account Selector */}
            {extensionConnected && payhipAccounts.length > 0 && (
              <select
                value={selectedPayhipAccount}
                onChange={(e) => setSelectedPayhipAccount(e.target.value)}
                style={{
                  padding: "6px 10px",
                  borderRadius: "4px",
                  fontSize: "12px",
                  border: "1px solid #ddd",
                  background: "#fff",
                  cursor: "pointer"
                }}
                title="Select Payhip account to post to"
              >
                {payhipAccounts.map(account => (
                  <option key={account.id} value={account.id}>
                    {account.name} ({account.blogName})
                  </option>
                ))}
              </select>
            )}
            {selectedRecipes.size > 0 && extensionConnected && (() => {
              const needToPost = recipes.filter(r => selectedRecipes.has(r.id) && !r.payhipPosted).length;
              const alreadyPosted = selectedRecipes.size - needToPost;
              return (
                <button
                  onClick={handleBulkPostToPayhip}
                  className={styles.payhipBtn}
                  style={{ background: needToPost === 0 ? "#9ca3af" : undefined }}
                  title={alreadyPosted > 0 ? `${alreadyPosted} already posted - will be skipped` : "Add to queue"}
                >
                  {needToPost > 0 ? `+Queue (${needToPost})` : `All Posted ✓`}
                </button>
              );
            })()}
            {/* AI Metadata Generation Button - Step 1: Generate AI metadata (including short pin title) */}
            {selectedRecipes.size > 0 && (() => {
              // AI Meta is for Payhip-posted recipes that don't have metadata yet
              const needMetadata = recipes.filter(r => selectedRecipes.has(r.id) && r.payhipPosted && !r.pinTitle).length;
              const haveMetadata = recipes.filter(r => selectedRecipes.has(r.id) && r.pinTitle).length;
              return (
                <button
                  onClick={handleGeneratePinterestMetadata}
                  disabled={isGeneratingMetadata}
                  style={{
                    padding: "8px 16px",
                    background: isGeneratingMetadata ? "#ccc" : needMetadata > 0 ? "#7c3aed" : "#9ca3af",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    cursor: isGeneratingMetadata ? "not-allowed" : "pointer",
                    fontSize: "13px",
                    fontWeight: "bold"
                  }}
                  title={haveMetadata > 0 ? `${haveMetadata} already have metadata - will be skipped` : "Step 1: Generate AI titles & descriptions"}
                >
                  {isGeneratingMetadata ? "Generating..." : needMetadata > 0 ? `1. AI Meta (${needMetadata})` : `AI Meta ✓`}
                </button>
              );
            })()}
            {/* Pinterest Pin Generation Button - Step 2: Generate pins using AI-generated short titles */}
            {selectedRecipes.size > 0 && (() => {
              // Pins require AI Meta first (pinTitle must exist)
              const needPins = recipes.filter(r => selectedRecipes.has(r.id) && r.payhipPosted && r.pinTitle && !r.pinUrl).length;
              const havePins = recipes.filter(r => selectedRecipes.has(r.id) && r.pinUrl).length;
              const needAIMeta = recipes.filter(r => selectedRecipes.has(r.id) && r.payhipPosted && !r.pinTitle && !r.pinUrl).length;
              return (
                <button
                  onClick={handleGeneratePins}
                  disabled={isGeneratingPins}
                  style={{
                    padding: "8px 16px",
                    background: isGeneratingPins ? "#ccc" : needPins > 0 ? "#e60023" : needAIMeta > 0 ? "#f59e0b" : "#9ca3af",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    cursor: isGeneratingPins ? "not-allowed" : "pointer",
                    fontSize: "13px",
                    fontWeight: "bold"
                  }}
                  title={havePins > 0 ? `${havePins} already have pins - will be skipped` : needAIMeta > 0 ? `${needAIMeta} need AI Meta first` : "Step 2: Generate Pinterest pins"}
                >
                  {isGeneratingPins ? "Generating..." : needPins > 0 ? `2. Pins (${needPins})` : needAIMeta > 0 ? `Pins (${needAIMeta} need AI)` : `Pins ✓`}
                </button>
              );
            })()}
            {/* Schedule to Metricool Controls */}
            {selectedRecipes.size > 0 && (
              <>
                <label style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "12px", color: "#666" }}>
                  <input
                    type="checkbox"
                    checked={useMultiDay}
                    onChange={(e) => setUseMultiDay(e.target.checked)}
                  />
                  Multi-day
                </label>
                <input
                  type="date"
                  value={scheduleStartDate}
                  onChange={(e) => setScheduleStartDate(e.target.value)}
                  style={{ padding: "6px 8px", fontSize: "12px", border: "1px solid #ddd", borderRadius: "4px" }}
                  title={useMultiDay ? "Start date" : "Schedule date"}
                />
                {useMultiDay && (
                  <>
                    <input
                      type="number"
                      value={pinsPerDay}
                      onChange={(e) => setPinsPerDay(Math.max(1, parseInt(e.target.value) || 1))}
                      min="1"
                      style={{ width: "50px", padding: "6px 8px", fontSize: "12px", border: "1px solid #ddd", borderRadius: "4px" }}
                      title="Pins per day"
                    />
                    <span style={{ fontSize: "11px", color: "#666" }}>pins/day</span>
                    {scheduleStartDate && (
                      <span style={{ fontSize: "11px", color: "#4caf50", fontWeight: "500" }}>
                        → {calculateFinishDate(scheduleStartDate, pinsPerDay, selectedRecipes.size)}
                      </span>
                    )}
                  </>
                )}
                {scheduleProgress && (
                  <span style={{ fontSize: "12px", color: "#4caf50", fontWeight: "bold" }}>
                    📤 {scheduleProgress.current}/{scheduleProgress.total}
                  </span>
                )}
              </>
            )}
            {/* Schedule to Metricool Button - always show when recipes selected */}
            {selectedRecipes.size > 0 && (() => {
              const needScheduling = recipes.filter(r => selectedRecipes.has(r.id) && r.pinUrl && r.pinterestTitle && !r.metricoolScheduled).length;
              const alreadyScheduled = recipes.filter(r => selectedRecipes.has(r.id) && r.metricoolScheduled).length;
              // Check if all selected recipes have properly configured accounts
              const recipesWithConfig = recipes.filter(r => {
                if (!selectedRecipes.has(r.id) || !r.pinUrl || !r.pinterestTitle) return false;
                const account = r.payhipAccountId
                  ? payhipAccounts.find(a => a.id === r.payhipAccountId)
                  : payhipAccounts.find(a => a.id === selectedPayhipAccount);
                return account?.metricoolKeyName && account?.pinterestBrandName && account?.pinterestBoards;
              }).length;
              const readyToSchedule = recipesWithConfig === needScheduling && needScheduling > 0;
              return (
                <button
                  onClick={handleScheduleToMetricool}
                  disabled={isSchedulingMetricool || (useMultiDay && !scheduleStartDate)}
                  style={{
                    padding: "8px 16px",
                    background: isSchedulingMetricool ? "#ccc" : readyToSchedule ? "#4caf50" : "#9ca3af",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    cursor: (isSchedulingMetricool || (useMultiDay && !scheduleStartDate)) ? "not-allowed" : "pointer",
                    fontSize: "13px",
                    fontWeight: "bold"
                  }}
                  title={alreadyScheduled > 0 ? `${alreadyScheduled} already scheduled - will be skipped` : (readyToSchedule ? "Schedule to Metricool (using each recipe's account)" : "Some recipes missing Metricool config")}
                >
                  {isSchedulingMetricool ? `Scheduling...` : needScheduling > 0 ? `Metricool (${needScheduling})` : `Scheduled ✓`}
                </button>
              );
            })()}
            <button onClick={() => fetchRecipes(1, searchQuery, selectedCategory)} className={styles.refreshBtn}>
              Refresh
            </button>
          </div>
        </div>
      )}

      {/* Pin Results Panel */}
      {showPinResults && pinResults.length > 0 && (
        <div style={{
          margin: "16px 0",
          padding: "16px",
          background: "#fef2f2",
          border: "2px solid #e60023",
          borderRadius: "8px"
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
            <h3 style={{ margin: 0, color: "#e60023" }}>
              Generated {pinResults.length} Pinterest Pins
            </h3>
            <div style={{ display: "flex", gap: "8px" }}>
              <button
                onClick={handleCopyPinUrls}
                style={{
                  padding: "8px 16px",
                  background: "#e60023",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontWeight: "bold"
                }}
              >
                Copy All URLs
              </button>
              <button
                onClick={() => setShowPinResults(false)}
                style={{
                  padding: "8px 16px",
                  background: "#666",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer"
                }}
              >
                Close
              </button>
            </div>
          </div>
          <div style={{
            maxHeight: "300px",
            overflowY: "auto",
            background: "white",
            borderRadius: "6px",
            padding: "8px"
          }}>
            {pinResults.map((pin, idx) => (
              <div key={idx} style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                padding: "8px",
                borderBottom: idx < pinResults.length - 1 ? "1px solid #eee" : "none"
              }}>
                <img
                  src={pin.pin_url}
                  alt={pin.title}
                  style={{ width: "50px", height: "90px", objectFit: "cover", borderRadius: "4px" }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: "bold", fontSize: "13px", marginBottom: "4px" }}>
                    {pin.title}
                  </div>
                  <a
                    href={pin.pin_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontSize: "11px", color: "#0066cc", wordBreak: "break-all" }}
                  >
                    {pin.pin_url}
                  </a>
                </div>
                <a
                  href={pin.pin_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    padding: "6px 12px",
                    background: "#e60023",
                    color: "white",
                    borderRadius: "4px",
                    textDecoration: "none",
                    fontSize: "12px",
                    fontWeight: "bold"
                  }}
                >
                  Open
                </a>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className={styles.error}>{error}</div>
      )}

      {/* Recipes Grid */}
      {recipes.length === 0 && !isLoading && !error && apiToken && (
        <div className={styles.emptyState}>
          No recipes found. Try a different search or check your API settings.
        </div>
      )}

      <div className={styles.recipesGrid}>
        {recipes
          .filter(recipe => (!showPayhipOnly || recipe.payhipPosted) && (!hideScheduled || !recipe.metricoolScheduled))
          .map((recipe) => (
          <div
            key={recipe.id}
            className={`${styles.recipeCard} ${selectedRecipes.has(recipe.id) ? styles.selected : ""}`}
          >
            <div className={styles.cardHeader}>
              <input
                type="checkbox"
                checked={selectedRecipes.has(recipe.id)}
                onChange={() => handleToggleSelection(recipe.id)}
                className={styles.checkbox}
              />
              {recipe.category && (
                <span className={styles.category}>{recipe.category}</span>
              )}
            </div>

            <div className={styles.imageWrapper}>
              <img
                src={recipe.image}
                alt={recipe.title}
                className={styles.recipeImage}
                loading="lazy"
              />
            </div>

            <div className={styles.cardContent}>
              <h3 className={styles.recipeTitle}>{recipe.title}</h3>
              <p className={styles.recipeDescription}>{recipe.firstParagraph}</p>

              <div className={styles.recipeUrl}>
                <a href={recipe.url} target="_blank" rel="noopener noreferrer">
                  {recipe.url}
                </a>
              </div>

              <div className={styles.cardActions}>
                <button
                  onClick={() => handleCopyRecipe(recipe)}
                  className={styles.copyBtn}
                >
                  {copiedId === recipe.id ? "Copied!" : "Copy"}
                </button>
                {/* Show Payhip status */}
                {recipe.payhipPosted && recipe.payhipUrl && (
                  <a
                    href={recipe.payhipUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.payhipCardBtn}
                    style={{ background: "#f97316", cursor: "pointer", textDecoration: "none" }}
                    title="View on Payhip"
                  >
                    PH ✓
                  </a>
                )}
                {recipe.payhipPosted && !recipe.payhipUrl && (
                  <span className={styles.payhipCardBtn} style={{ background: "#f97316", cursor: "default" }}>
                    Posted
                  </span>
                )}
                {extensionConnected && !postedRecipes.has(recipe.id) && !recipe.payhipPosted && (
                  <button
                    onClick={() => handlePostToPayhip(recipe)}
                    className={styles.payhipCardBtn}
                  >
                    +Queue
                  </button>
                )}
                {postedRecipes.has(recipe.id) && !recipe.payhipPosted && (
                  <button
                    onClick={() => handleMarkAsPosted(recipe)}
                    className={styles.payhipCardBtn}
                    style={{ background: "#22c55e" }}
                    title="Click to mark as posted and enter Payhip URL"
                  >
                    Queued - Mark Done
                  </button>
                )}
                {/* Metricool Scheduled indicator */}
                {recipe.metricoolScheduled && (
                  <span style={{
                    padding: "6px 10px",
                    background: "#4caf50",
                    color: "white",
                    borderRadius: "4px",
                    fontSize: "12px",
                    fontWeight: "bold"
                  }} title="Scheduled to Metricool">
                    ✓ Done
                  </span>
                )}
                {/* Pinterest Pin Link - show from DB or generated */}
                {(recipe.pinUrl || generatedPins.has(recipe.id)) && (
                  <a
                    href={recipe.pinUrl || generatedPins.get(recipe.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      padding: "6px 10px",
                      background: "#e60023",
                      color: "white",
                      borderRadius: "4px",
                      fontSize: "12px",
                      textDecoration: "none",
                      fontWeight: "bold"
                    }}
                    title="View Pinterest Pin"
                  >
                    PIN
                  </a>
                )}
                {/* Pinterest Metadata indicator - click to view */}
                {recipe.pinterestTitle && recipe.pinterestDescription && (
                  <button
                    onClick={() => {
                      alert(`📌 Pinterest Title:\n${recipe.pinterestTitle}\n\n📝 Description:\n${recipe.pinterestDescription}`);
                    }}
                    style={{
                      padding: "6px 10px",
                      background: "#7c3aed",
                      color: "white",
                      borderRadius: "4px",
                      fontSize: "12px",
                      fontWeight: "bold",
                      cursor: "pointer",
                      border: "none"
                    }}
                    title="Click to view Pinterest metadata"
                  >
                    AI ✓
                  </button>
                )}
                <a
                  href={recipe.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.viewBtn}
                >
                  View
                </a>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Loading */}
      {isLoading && (
        <div className={styles.loading}>Loading recipes...</div>
      )}

      {/* Load More */}
      {hasMore && recipes.length > 0 && !isLoading && (
        <div className={styles.loadMore}>
          <button onClick={handleLoadMore} className={styles.loadMoreBtn}>
            Load More
          </button>
        </div>
      )}

      {/* Stats */}
      {recipes.length > 0 && (
        <div className={styles.stats}>
          Showing {recipes.length} recipes from {selectedWebsite.name}
        </div>
      )}

    </div>
  );
}
